/* ============================================================
   hoh-fx-canvas.js — R448 · THE PARTICLE TABLE (canvas layer)
   FramedFantasy Hall of Heroes · Aug 2026

   WHY THIS EXISTS. Battle sparks used to be DOM spans — one element, one
   layout pass and one composite per particle, dozens of times per fight,
   mostly on phones. This file draws them all on ONE full-screen canvas:
   thousands of particles cost less than dozens of divs, and richer
   effects (trails, gravity, glow) become affordable for future drops.

   ============================================================
   HOW TO ADD AN EFFECT — read this before adding anything anywhere else.
   This registry is the ONLY place a new particle type goes. Never bolt a
   new DOM particle system onto hoh-contract-ui.js or hoh-fresh-faces.js;
   define it here and call it from there.

     1. Add one entry to EFFECTS below. An effect is three functions:
          spawn(o)      -> return an array of particle objects. Plain
                           data, any fields you want. o is whatever the
                           caller passed to emit().
          step(p, dt)   -> advance the particle by dt seconds. Return
                           false to kill it, true to keep it.
          draw(ctx, p)  -> paint it. ctx is already DPR-scaled, in CSS
                           pixel coordinates.
     2. Fire it from anywhere in any Hall file:
          window.hohFx.emit('yourname', { x: 100, y: 200, ... })
        emit returns true when the canvas took the job, false when the
        caller should run its own fallback (reduced motion, load failure).
     3. Done. The rAF loop, canvas creation, resize + devicePixelRatio,
        reduced-motion, idle teardown and the particle cap are owned here
        and never need touching.

   A third-party file can also register without editing this one:
     window.hohFx.define('mynewfx', { spawn:…, step:…, draw:… })

   LAWS (keep these or phones pay):
   - Reduced motion: this file reports available:false and draws nothing.
     Callers keep their own reduced-motion decisions for fallbacks.
   - The canvas is pointer-events:none at z-index 100060. Blocking inspector
     and guide sheets suppress it, while result celebrations remain visible.
   - The loop runs ONLY while particles are alive. Zero idle cost.
   - PART_CAP bounds the pool; when full, new spawns replace the oldest.
   - Every caller keeps a DOM fallback. This file failing to load must
     never delete the juice — same fallback discipline as R446/R447.
   ============================================================ */
(function () {
  'use strict';
  if (window.hohFx) return;

  /* 2026-09-28 (BFX-8): reduced motion is the OS setting OR the Battlegrounds page's own Motion setting (html.bg-rm,
     as bg-ui and bg-fight117 read it); either one turns the canvas off (motionApply keeps `available` in step) */
  var reduced = false, motionQuery = null, osReduced = false;
  function pageReduced() {
    try { return document.documentElement.classList.contains('bg-rm'); } catch (_) { return false; }
  }
  try {
    motionQuery = matchMedia('(prefers-reduced-motion: reduce)');
    osReduced = motionQuery.matches;
  } catch (e) {}
  reduced = osReduced || pageReduced();

  var PART_CAP = 1500;
  /* A semantic encounter cue is intentionally much smaller than the global
     pool. One content row can never turn a typo such as n:99999 into a phone
     freeze, while several simultaneous combat cues may still share PART_CAP. */
  var PRESENTATION_MOTE_CAP = 40;
  var canvas = null, ctx = null, parts = [], raf = 0, lastT = 0;

  function ensureCanvas() {
    if (canvas) return true;
    try {
      canvas = document.createElement('canvas');
      canvas.id = 'hoh-fx-canvas';
      canvas.style.cssText = 'position:fixed;inset:0;width:100%;height:100%;' +
        'pointer-events:none;z-index:100060;';
      document.body.appendChild(canvas);
      ctx = canvas.getContext('2d');
      resize();
      window.addEventListener('resize', resize);
      /* a hidden tab gets its particles cleared rather than a burst of
         catch-up motion when it returns */
      document.addEventListener('visibilitychange', function () {
        if (document.hidden) { parts.length = 0; stop(); }
      });
      return true;
    } catch (e) { canvas = null; ctx = null; return false; }
  }

  /* 2026-09-28 (smoothness): the canvas's CSS size is kept from the last resize. Every clear asked
     innerWidth/innerHeight, which Chrome answers with a forced style and layout, once or twice a frame. */
  var cssW = 0, cssH = 0;
  function resize() {
    if (!canvas) return;
    var dpr = Math.max(1, Math.min(3, window.devicePixelRatio || 1));
    cssW = innerWidth; cssH = innerHeight;
    canvas.width = Math.round(cssW * dpr);
    canvas.height = Math.round(cssH * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }
  function clearAll() { if (ctx) ctx.clearRect(0, 0, cssW || innerWidth, cssH || innerHeight); }
  /* 2026-10-01 (Battlegrounds PERF item 7): with nothing playing, the cleared canvas is hidden, so the screen's other
     animations stop blending a transparent full-screen layer (1170 x 2532 on a 3x phone) into every frame they draw.
     It shows again, already clear, as the next burst starts. */
  function rest() { if (canvas && canvas.style.visibility !== 'hidden') canvas.style.visibility = 'hidden'; }
  function wake() { if (canvas && canvas.style.visibility === 'hidden') canvas.style.visibility = ''; }

  /* 2026-09-28 (smoothness): read every frame, so it asks only where a blocking sheet can stand: the
     guide by its id, the battle's sheets (the plan and a hero's inspector) as children of the battle
     host, which is where both are mounted, and the confirm modal by its id. A document-wide
     querySelector walked the whole page on every frame no sheet was open (0.72 s a round at 4x CPU). */
  function blockingSheetOpen() {
    try {
      if (document.getElementById('hocGuide')) return true;
      var h = document.getElementById('hoh-contract');
      for (var c = h && h.firstElementChild; c; c = c.nextElementSibling) if (c.classList.contains('hoc-sheet')) return true;
      var confirm = document.getElementById('hoh-ff-confirm-modal');
      return !!(confirm && confirm.style.display !== 'none' && confirm.getAttribute('aria-hidden') !== 'true');
    } catch (_) { return false; }
  }

  /* ---------------- the effects registry ---------------- */
  /* R502 PRESENTATION CONTRACT. Encounter and ability data name a semantic
     presentation id; they never choose particle counts, colors or geometry.
     Every id below owns a distinct recipe, while one renderer keeps the phone
     budget, reduced-motion behavior and lifecycle in one place. This is the
     same data/behavior split as the combat profiles: adding content means
     adding a checked recipe, not another animation branch in the battle UI. */
  function own(obj, key) {
    return Object.prototype.hasOwnProperty.call(obj, key);
  }
  function finiteNumber(value, fallback) {
    return typeof value === 'number' && isFinite(value) ? value : fallback;
  }
  function viewportMiddle(axis) {
    var size = axis === 'x' ?
      (typeof innerWidth === 'number' ? innerWidth : 0) :
      (typeof innerHeight === 'number' ? innerHeight : 0);
    return size / 2;
  }
  function presentationMoteCap(recipe) {
    return Math.max(4, Math.min(PRESENTATION_MOTE_CAP,
      finiteNumber(recipe.cap, 30) | 0));
  }
  function themedFx(recipe) {
    recipe = recipe || {};
    return {
      spawn: function (o) {
        o = o || {};
        var moteCap = presentationMoteCap(recipe);
        var requested = finiteNumber(o.n, 0) | 0;
        if (requested <= 0) requested = finiteNumber(recipe.n, 16) | 0;
        var out = [], n = Math.max(4, Math.min(moteCap, requested));
        var x = finiteNumber(o.x, viewportMiddle('x'));
        var y = finiteNumber(o.y, viewportMiddle('y'));
        var col = typeof o.color === 'string' ? o.color : (recipe.color || '#e8c674');
        var hot = recipe.hot || '#fff6d8';
        var rail = recipe.rail === true;
        var spread = finiteNumber(o.spread, finiteNumber(recipe.spread, 86));
        var spreadMax = rail ? Math.max(120, Math.min(720,
          (typeof innerWidth === 'number' ? innerWidth : 390) - 32)) : 220;
        spread = Math.max(rail ? 120 : 18, Math.min(spreadMax, spread));
        if (recipe.ring !== false) {
          out.push({ themedRing: 1, x: x, y: y, r: 5,
            vr: recipe.ringSpeed || 230, life: 0, ttl: recipe.ringTtl || 0.46,
            w: recipe.ringWidth || 2.2, color: col });
        }
        for (var i = 0; i < n; i++) {
          var a = rail ? -1.5708 + (Math.random() - 0.5) * 0.65 :
            6.2832 * i / n + Math.random() * 0.35;
          var speed = (recipe.speed || 78) * (0.65 + Math.random() * 0.7);
          var lift = recipe.lift == null ? 34 : recipe.lift;
          var vx = Math.cos(a) * speed, vy = Math.sin(a) * speed - lift;
          var mote = { themed: 1, shape: recipe.shape || 'mote', spin: recipe.spin || 0,
            x: x + (rail ? ((i + 0.5) / n - 0.5) * spread :
              (Math.random() - 0.5) * spread * 0.18),
            y: y + (Math.random() - 0.5) * spread * 0.12,
            px: x, py: y,
            vx: vx, vy: vy,
            life: -(Math.random() * (recipe.stagger || 0.12)),
            ttl: (recipe.ttl || 0.75) * (0.8 + Math.random() * 0.35),
            r: (recipe.size || 2.2) * (0.75 + Math.random() * 0.55),
            color: Math.random() < (recipe.hotChance || 0.24) ? hot : col };
          if (recipe.orient) mote.a = Math.atan2(vy, vx);
          out.push(mote);
        }
        return out;
      },
      step: function (p, dt) {
        p.life += dt;
        if (p.life < 0) return true;
        if (p.themedRing) {
          p.r += p.vr * dt; p.vr *= (1 - 2.9 * dt);
          return p.life < p.ttl;
        }
        p.px = p.x; p.py = p.y;
        p.x += p.vx * dt; p.y += p.vy * dt;
        p.vx *= (1 - 2.25 * dt);
        p.vy = p.vy * (1 - 1.5 * dt) +
          (recipe.gravity == null ? 90 : recipe.gravity) * dt;
        p.a = (p.a || 0) + (p.spin || 0) * dt;
        return p.life < p.ttl;
      },
      draw: function (c, p) {
        if (p.life < 0) return;
        var k = Math.max(0, 1 - p.life / p.ttl);
        if (p.themedRing) {
          c.globalAlpha = k * 0.78; c.strokeStyle = p.color;
          c.lineWidth = p.w * k + 0.35;
          c.beginPath(); c.arc(p.x, p.y, p.r, 0, 6.2832); c.stroke();
          return;
        }
        c.globalAlpha = k * 0.9; c.fillStyle = p.color; c.strokeStyle = p.color;
        c.lineWidth = Math.max(0.6, p.r * 0.45);
        c.save(); c.translate(p.x, p.y); c.rotate(p.a || 0);
        if (p.shape === 'petal') {
          c.beginPath(); c.ellipse(0, 0, p.r * 0.72, p.r * 1.75, 0, 0, 6.2832); c.fill();
        } else if (p.shape === 'gear') {
          c.beginPath(); c.rect(-p.r, -p.r, p.r * 2, p.r * 2); c.stroke();
          c.beginPath(); c.arc(0, 0, p.r * 0.35, 0, 6.2832); c.fill();
        } else if (p.shape === 'shard') {
          c.beginPath(); c.moveTo(0, -p.r * 1.6); c.lineTo(p.r, p.r);
          c.lineTo(-p.r, p.r); c.closePath(); c.fill();
        } else if (p.shape === 'arrow') {
          c.beginPath(); c.moveTo(-p.r * 1.7, 0); c.lineTo(p.r * 1.45, 0);
          c.moveTo(p.r * 0.55, -p.r * 0.8); c.lineTo(p.r * 1.45, 0);
          c.lineTo(p.r * 0.55, p.r * 0.8); c.stroke();
        } else if (p.shape === 'rune') {
          c.beginPath(); c.moveTo(-p.r, 0); c.lineTo(0, -p.r);
          c.lineTo(p.r, 0); c.lineTo(0, p.r); c.closePath(); c.stroke();
        } else if (p.shape === 'horn') {
          c.beginPath(); c.arc(-p.r * 0.35, 0, p.r, -1.1, 1.1); c.stroke();
        } else if (p.shape === 'vine') {
          c.lineCap = 'round';
          c.beginPath(); c.moveTo(-p.r * 0.25, p.r * 1.8);
          c.lineTo(p.r * 0.55, p.r * 0.75); c.lineTo(-p.r * 0.45, -p.r * 0.15);
          c.lineTo(p.r * 0.25, -p.r * 1.75); c.stroke();
          c.beginPath(); c.moveTo(p.r * 0.4, p.r * 0.6);
          c.lineTo(p.r * 1.2, p.r * 0.2); c.moveTo(-p.r * 0.35, -p.r * 0.3);
          c.lineTo(-p.r * 1.1, -p.r * 0.75); c.stroke();
        } else {
          c.beginPath(); c.arc(0, 0, p.r, 0, 6.2832); c.fill();
        }
        c.restore();
      }
    };
  }
  var EFFECTS = {
    /* the battle burst: n dots leaving a center radially, easing out,
       fading over ~0.65s — the visual twin of the old .hoc-sp span burst */
    sparks: {
      spawn: function (o) {
        var out = [], n = Math.max(1, Math.min(60, o.n | 0 || 10));
        for (var i = 0; i < n; i++) {
          var a = Math.PI * 2 * i / n + Math.random() * 0.5;
          var sp = 40 + Math.random() * 55;
          out.push({
            x: o.x, y: o.y,
            vx: Math.cos(a) * sp, vy: Math.sin(a) * sp,
            life: 0, ttl: 0.55 + Math.random() * 0.25,
            r: 1.6 + Math.random() * 1.4,
            color: o.color || '#e8c674'
          });
        }
        return out;
      },
      step: function (p, dt) {
        p.life += dt;
        p.x += p.vx * dt; p.y += p.vy * dt;
        p.vx *= (1 - 2.4 * dt); p.vy *= (1 - 2.4 * dt);
        return p.life < p.ttl;
      },
      draw: function (c, p) {
        c.globalAlpha = Math.max(0, 1 - p.life / p.ttl);
        c.fillStyle = p.color;
        c.beginPath();
        c.arc(p.x, p.y, p.r, 0, 6.2832);
        c.fill();
      }
    },
    /* R450b 'cardplay' — a CAST. One expanding ring shockwave plus a fan
       of streaks with short trails, colored by the class that played it.
       Fired at class-card resolution and any battle moment that should
       read as a spell leaving a hand. opts: x, y, color, n (streaks),
       big (1 = a heavier ring for signature cards). */
    cardplay: {
      spawn: function (o) {
        var out = [], n = Math.max(6, Math.min(30, o.n | 0 || 16));
        var col = o.color || '#e8c674';
        out.push({ ring: 1, x: o.x, y: o.y, r: 6, vr: o.big ? 340 : 240,
                   life: 0, ttl: o.big ? 0.5 : 0.4, w: o.big ? 3 : 2, color: col });
        for (var i = 0; i < n; i++) {
          var a = Math.PI * 2 * i / n + Math.random() * 0.4;
          var sp = 90 + Math.random() * 120;
          out.push({
            x: o.x, y: o.y, px: o.x, py: o.y,
            vx: Math.cos(a) * sp, vy: Math.sin(a) * sp - 30,
            life: 0, ttl: 0.5 + Math.random() * 0.3,
            r: 1.4 + Math.random() * 1.6, color: col
          });
        }
        return out;
      },
      step: function (p, dt) {
        p.life += dt;
        if (p.ring) { p.r += p.vr * dt; p.vr *= (1 - 3.4 * dt); return p.life < p.ttl; }
        p.px = p.x; p.py = p.y;
        p.x += p.vx * dt; p.y += p.vy * dt;
        p.vx *= (1 - 2.8 * dt); p.vy = p.vy * (1 - 2.8 * dt) + 150 * dt;
        return p.life < p.ttl;
      },
      draw: function (c, p) {
        var k = Math.max(0, 1 - p.life / p.ttl);
        if (p.ring) {
          c.globalAlpha = k * 0.85;
          c.strokeStyle = p.color; c.lineWidth = p.w * k + 0.4;
          c.beginPath(); c.arc(p.x, p.y, p.r, 0, 6.2832); c.stroke();
          return;
        }
        c.globalAlpha = k;
        c.strokeStyle = p.color; c.lineWidth = p.r;
        c.lineCap = 'round';
        c.beginPath(); c.moveTo(p.px, p.py); c.lineTo(p.x, p.y); c.stroke();
      }
    },
    /* ============ R457 · THE BATTLE HAS WEATHER ============
       Jan: "there needs to be added particle effects and animations during
       the battle, its too boring right now."
       Four effects, and each one answers a DIFFERENT question the board was
       failing to answer at a glance:
         slash   — WHO hit WHO. A blow used to be a number appearing on one
                   side with nothing connecting it to the tile that threw it.
         impact  — HOW HARD. Sized and coloured by the damage, so a 9 and a
                   40 no longer look the same.
         shock   — THIS ONE MATTERED. Crits, perfect rolls and kills.
         embers  — the board is a place. Fired as a short puff on a round
                   change, never as an idle loop, because this file's law is
                   zero cost while nothing is alive.
       All four are cheap: no gradients, no shadowBlur, no per-particle
       save/restore. The heaviest (impact at n 26) spawns 27 objects. */

    /* a blow travelling from the attacker to the target: one bright leading
       segment that stretches, whips past the target and snaps out.
       opts: x, y (from), tx, ty (to), color, w (line width). */
    slash: {
      spawn: function (o) {
        var dx = (o.tx == null ? o.x : o.tx) - o.x;
        var dy = (o.ty == null ? o.y : o.ty) - o.y;
        var len = Math.sqrt(dx * dx + dy * dy) || 1;
        return [{
          x: o.x, y: o.y, ux: dx / len, uy: dy / len, len: len,
          life: 0, ttl: 0.26, w: Math.max(1.5, Math.min(5, o.w || 3)),
          color: o.color || '#e8c674'
        }];
      },
      step: function (p, dt) { p.life += dt; return p.life < p.ttl; },
      draw: function (c, p) {
        var k = p.life / p.ttl;
        /* the head runs the whole distance in the first 60% and the tail
           chases it, so the streak is short at both ends and long in the
           middle — a whip, not a growing bar */
        var head = Math.min(1, k / 0.6);
        var tail = Math.max(0, (k - 0.28) / 0.72);
        var hx = p.x + p.ux * p.len * head, hy = p.y + p.uy * p.len * head;
        var tx = p.x + p.ux * p.len * tail, ty = p.y + p.uy * p.len * tail;
        c.globalAlpha = Math.max(0, 1 - k) * 0.9;
        c.strokeStyle = p.color;
        c.lineWidth = p.w * Math.max(0.25, 1 - k);
        c.lineCap = 'round';
        c.beginPath(); c.moveTo(tx, ty); c.lineTo(hx, hy); c.stroke();
      }
    },

    /* the blow LANDING. A cone of shards thrown back along the blow's own
       direction plus a short-lived core, both sized by `big` (0..1, the
       caller's own damage scale). opts: x, y, dir (radians, the direction the
       blow was travelling), n, color, big. */
    impact: {
      spawn: function (o) {
        var out = [], big = Math.max(0, Math.min(1, o.big == null ? 0.5 : o.big));
        var n = Math.max(5, Math.min(26, (o.n | 0) || Math.round(8 + 16 * big)));
        var dir = (typeof o.dir === 'number') ? o.dir : -Math.PI / 2;
        var col = o.color || '#c0564a';
        out.push({ core: 1, x: o.x, y: o.y, r: 5 + 16 * big, life: 0, ttl: 0.2, color: col });
        for (var i = 0; i < n; i++) {
          /* a 130 degree fan around the blow direction: shards spray FORWARD
             off the hit, which is what makes the direction readable */
          var a = dir + (Math.random() - 0.5) * 2.3;
          var sp = (70 + Math.random() * 190) * (0.55 + big * 0.75);
          out.push({
            x: o.x, y: o.y, px: o.x, py: o.y,
            vx: Math.cos(a) * sp, vy: Math.sin(a) * sp - 40,
            life: 0, ttl: 0.34 + Math.random() * 0.32,
            r: 1 + Math.random() * (1.2 + big * 1.8), color: col
          });
        }
        return out;
      },
      step: function (p, dt) {
        p.life += dt;
        if (p.core) return p.life < p.ttl;
        p.px = p.x; p.py = p.y;
        p.x += p.vx * dt; p.y += p.vy * dt;
        p.vx *= (1 - 3.1 * dt); p.vy = p.vy * (1 - 1.6 * dt) + 620 * dt;   /* gravity */
        return p.life < p.ttl;
      },
      draw: function (c, p) {
        var k = Math.max(0, 1 - p.life / p.ttl);
        if (p.core) {
          c.globalAlpha = k * 0.5;
          c.fillStyle = p.color;
          c.beginPath(); c.arc(p.x, p.y, p.r * (0.4 + 0.6 * (1 - k)), 0, 6.2832); c.fill();
          return;
        }
        c.globalAlpha = k;
        c.strokeStyle = p.color; c.lineWidth = p.r; c.lineCap = 'round';
        c.beginPath(); c.moveTo(p.px, p.py); c.lineTo(p.x, p.y); c.stroke();
      }
    },

    /* THIS ONE MATTERED. Two rings leaving the same point at different
       speeds, so the moment reads as a pressure wave rather than a circle.
       opts: x, y, color, big (1 = a kill or a perfect roll). */
    shock: {
      spawn: function (o) {
        var col = o.color || '#e8c674', big = o.big ? 1 : 0;
        return [
          { x: o.x, y: o.y, r: 8, vr: big ? 620 : 420, life: 0, ttl: big ? 0.5 : 0.38, w: big ? 3.5 : 2.4, color: col },
          { x: o.x, y: o.y, r: 4, vr: big ? 340 : 230, life: 0, ttl: big ? 0.62 : 0.46, w: big ? 2 : 1.4, color: col }
        ];
      },
      step: function (p, dt) { p.life += dt; p.r += p.vr * dt; p.vr *= (1 - 2.6 * dt); return p.life < p.ttl; },
      draw: function (c, p) {
        var k = Math.max(0, 1 - p.life / p.ttl);
        c.globalAlpha = k * 0.8;
        c.strokeStyle = p.color; c.lineWidth = p.w * k + 0.3;
        c.beginPath(); c.arc(p.x, p.y, p.r, 0, 6.2832); c.stroke();
      }
    },

    /* the board breathes. A short puff of slow motes rising through a box,
       fired on a round change and on a sheet opening — never on a timer, so
       the loop still sleeps whenever nothing is alive.
       opts: x, y (centre), w, h (the box to fill), n, color. */
    embers: {
      spawn: function (o) {
        var out = [], n = Math.max(3, Math.min(24, (o.n | 0) || 10));
        var w = o.w || 160, h = o.h || 90, col = o.color || '#e8c674';
        for (var i = 0; i < n; i++) {
          out.push({
            x: o.x + (Math.random() - 0.5) * w,
            y: o.y + (Math.random() - 0.5) * h,
            vy: -(10 + Math.random() * 26),
            drift: (Math.random() - 0.5) * 22,
            life: 0, ttl: 1.5 + Math.random() * 1.4,
            r: 0.7 + Math.random() * 1.3, color: col
          });
        }
        return out;
      },
      step: function (p, dt) {
        p.life += dt;
        p.y += p.vy * dt;
        p.x += Math.sin(p.life * 1.7) * p.drift * dt;
        return p.life < p.ttl;
      },
      draw: function (c, p) {
        var k = p.life / p.ttl;
        c.globalAlpha = (k < 0.2 ? k / 0.2 : Math.max(0, 1 - (k - 0.2) / 0.8)) * 0.55;
        c.fillStyle = p.color;
        c.beginPath(); c.arc(p.x, p.y, p.r, 0, 6.2832); c.fill();
      }
    },

    /* R456 'deal' — a card travelling from a hero's tile into the hand.
       Fired once per party member at the top of a battle, so five of these
       run staggered. It is a comet, not a burst: a bright head on a fading
       trail that flies from (x,y) to (tx,ty) along a slight arc, because a
       straight line between two points on a phone reads as a glitch rather
       than as a thing being handed over.
       opts: x, y (from), tx, ty (to), color, delay (seconds). */
    /* R459 'chain' — THE BONUS-ACTION LINK.
       Jan: "have the actions bonus chain together since there are so many ...
       make it faster and more satisfying with particle effects."
       A bolt from the body that just acted to the body acting now, so a run of
       nine draws itself as ONE connected sweep instead of nine unrelated
       flashes. It brightens and thickens as the run goes on (step / of), which
       is the visual half of the same acceleration the timing does.
       Deliberately cheap: one travelling head, a short static bolt that fades,
       and four motes. A nine-body run fires this eight times inside about two
       and a half seconds, so it has to cost almost nothing on a phone.
       opts: x, y (from), tx, ty (to), color, step, of. */
    chain: {
      spawn: function (o) {
        var out = [], col = o.color || '#8fe0a5';
        var of = Math.max(2, o.of || 3), step = Math.max(1, o.step || 1);
        /* 0 at the start of the run, 1 at its end */
        var heat = Math.min(1, step / (of - 1));
        var dx = (o.tx || o.x) - o.x, dy = (o.ty || o.y) - o.y;
        var len = Math.sqrt(dx * dx + dy * dy) || 1;
        /* R775: o.ttl (seconds) is how long the head takes to arrive, so a
           caller whose number moves on arrival can fit the flight inside its
           beat at any speed; without it the flight is the 0.2s it always was */
        var travel = o.ttl > 0 ? Math.max(0.06, Math.min(0.4, +o.ttl)) : 0.2;
        /* the bolt itself: drawn whole, fades fast, thickens with the run */
        out.push({ bolt: 1, x0: o.x, y0: o.y, x1: (o.tx || o.x), y1: (o.ty || o.y),
                   jitter: Math.min(14, len * 0.09), heat: heat,
                   life: 0, ttl: travel * 1.2, color: col, r: 1.1 + heat * 1.5 });
        /* the head, which is what the eye actually follows along the run */
        out.push({ comet: 1, x: o.x, y: o.y, px: o.x, py: o.y,
                   x0: o.x, y0: o.y, dx: dx, dy: dy,
                   life: 0, ttl: travel, color: col, r: 2.2 + heat * 1.6 });
        for (var i = 0; i < 4; i++) {
          out.push({ x: (o.tx || o.x), y: (o.ty || o.y), px: (o.tx || o.x), py: (o.ty || o.y),
                     vx: (Math.random() - 0.5) * 90, vy: (Math.random() - 0.5) * 90 - 20,
                     life: -travel * 0.7, ttl: 0.26 + Math.random() * 0.16,
                     r: 0.9 + Math.random() * (1 + heat), color: col });
        }
        return out;
      },
      step: function (p, dt) {
        p.life += dt;
        if (p.life < 0) return true;
        if (p.bolt) return p.life < p.ttl;
        if (p.comet) {
          var k = Math.min(1, p.life / p.ttl);
          p.px = p.x; p.py = p.y;
          p.x = p.x0 + p.dx * k; p.y = p.y0 + p.dy * k;
          return p.life < p.ttl;
        }
        p.px = p.x; p.py = p.y;
        p.x += p.vx * dt; p.y += p.vy * dt;
        p.vx *= (1 - 3.4 * dt); p.vy = p.vy * (1 - 3.4 * dt) + 120 * dt;
        return p.life < p.ttl;
      },
      draw: function (c, p) {
        if (p.life < 0) return;
        var k = Math.max(0, 1 - p.life / p.ttl);
        c.lineCap = 'round';
        if (p.bolt) {
          /* three kinked segments so it reads as a discharge and not as a
             ruler line. The kink is seeded off life so it does not crawl. */
          c.globalAlpha = k * (0.45 + p.heat * 0.45);
          c.strokeStyle = p.color; c.lineWidth = p.r * k + 0.4;
          var mx = (p.x0 + p.x1) / 2, my = (p.y0 + p.y1) / 2;
          var nx = -(p.y1 - p.y0), ny = (p.x1 - p.x0);
          var nl = Math.sqrt(nx * nx + ny * ny) || 1;
          var j = p.jitter * (1 - k);
          c.beginPath();
          c.moveTo(p.x0, p.y0);
          c.lineTo(mx + (nx / nl) * j, my + (ny / nl) * j);
          c.lineTo(p.x1, p.y1);
          c.stroke();
          return;
        }
        c.globalAlpha = p.comet ? Math.min(1, 0.4 + k) : k;
        c.strokeStyle = p.color; c.lineWidth = p.r * (p.comet ? 1 : k) + 0.3;
        c.beginPath(); c.moveTo(p.px, p.py); c.lineTo(p.x, p.y); c.stroke();
        if (p.comet) {
          c.globalAlpha = k; c.fillStyle = p.color;
          c.beginPath(); c.arc(p.x, p.y, p.r * 0.85, 0, 6.2832); c.fill();
        }
      }
    },
    deal: {
      spawn: function (o) {
        var out = [], col = o.color || '#e8c674';
        var dx = (o.tx || o.x) - o.x, dy = (o.ty || o.y) - o.y;
        /* the arc bows perpendicular to the flight, scaled to its length and
           bounded so a long throw does not loop off the top of the screen */
        var bow = Math.max(18, Math.min(90, Math.sqrt(dx * dx + dy * dy) * 0.22));
        out.push({ comet: 1, x: o.x, y: o.y, px: o.x, py: o.y,
                   x0: o.x, y0: o.y, dx: dx, dy: dy, bow: bow,
                   life: -(o.delay || 0), ttl: 0.42, color: col, r: 3.2 });
        for (var i = 0; i < 7; i++) {
          out.push({ x: o.x, y: o.y, px: o.x, py: o.y,
                     vx: (Math.random() - 0.5) * 70, vy: (Math.random() - 0.5) * 70,
                     life: -(o.delay || 0), ttl: 0.3 + Math.random() * 0.2,
                     r: 1 + Math.random(), color: col });
        }
        return out;
      },
      step: function (p, dt) {
        p.life += dt;
        if (p.life < 0) return true;                 /* still waiting its turn */
        if (p.comet) {
          var k = Math.min(1, p.life / p.ttl);
          var e = 1 - Math.pow(1 - k, 3);            /* ease out, so it lands soft */
          p.px = p.x; p.py = p.y;
          p.x = p.x0 + p.dx * e;
          p.y = p.y0 + p.dy * e - Math.sin(e * Math.PI) * p.bow;
          return p.life < p.ttl;
        }
        p.px = p.x; p.py = p.y;
        p.x += p.vx * dt; p.y += p.vy * dt;
        p.vx *= (1 - 3 * dt); p.vy = p.vy * (1 - 3 * dt) + 90 * dt;
        return p.life < p.ttl;
      },
      draw: function (c, p) {
        if (p.life < 0) return;
        var k = Math.max(0, 1 - p.life / p.ttl);
        c.globalAlpha = p.comet ? Math.min(1, 0.35 + k) : k;
        c.strokeStyle = p.color; c.lineWidth = p.r * (p.comet ? 1 : k) + 0.3;
        c.lineCap = 'round';
        c.beginPath(); c.moveTo(p.px, p.py); c.lineTo(p.x, p.y); c.stroke();
        if (p.comet) {
          c.globalAlpha = k;
          c.fillStyle = p.color;
          c.beginPath(); c.arc(p.x, p.y, p.r * 0.9, 0, 6.2832); c.fill();
        }
      }
    },
    /* Engine 84 'merge' — THE BENCH STACKS DOWN. Three bench tiles become one:
       a comet leaves each absorbed tile and converges on the survivor along a
       soft arc with a short trail, and the landing blooms a ring in the new
       rarity's colour with a small fountain. Deliberately cheap: at most six
       comets, a handful of motes each, two rings and one fountain, because a
       first consolidation can fire this dozens of times inside two seconds.
       opts: x, y (the survivor's centre), from [{x, y}] (the absorbed tiles),
       color, big (legendary or mythic), quick (a folded merge: no fountain). */
    /* Engine 85 · THE SEAT BREAKS OPEN. The merge's own physics, pointed the
       other way: instead of comets converging on a seat, shards leave it. Two
       rings and a shard fan, because what the eye has to read here is "one
       thing became several" and a single ring says only "something happened".
       It reuses merge's step and draw wholesale, so a shard and a comet obey
       the same drag and land in the same pool budget. */
    shatter: {
      spawn: function (o) {
        var out = [], col = o.color || '#8a8090';
        var n = Math.max(2, Math.min(3, o.n | 0)) || 3;
        /* one heavy shard per piece, thrown on an even fan so three pieces
           read as three even before their tiles arrive */
        for (var s = 0; s < n; s++) {
          var a = -Math.PI / 2 + (s - (n - 1) / 2) * 0.62;
          var sp = o.big ? 250 : 190;
          out.push({ x: o.x, y: o.y, px: o.x, py: o.y,
                     vx: Math.cos(a) * sp, vy: Math.sin(a) * sp,
                     life: 0, ttl: 0.46, r: o.big ? 3.2 : 2.4, color: col, spark: 1 });
        }
        out.push({ ring: 1, x: o.x, y: o.y, r: 3, vr: o.big ? 460 : 340, life: 0,
                   ttl: o.big ? 0.5 : 0.4, w: o.big ? 3 : 2.1, color: col });
        out.push({ ring: 1, x: o.x, y: o.y, r: 2, vr: 210, life: 0.07, ttl: 0.44, w: 1.3, color: '#fff6d8' });
        var dust = o.big ? 26 : 18;
        for (var k = 0; k < dust; k++) {
          var da = Math.PI * 2 * k / dust + Math.random() * 0.5, ds = 70 + Math.random() * 120;
          out.push({ x: o.x, y: o.y, px: o.x, py: o.y, vx: Math.cos(da) * ds, vy: Math.sin(da) * ds - 30,
                     life: 0, ttl: 0.42 + Math.random() * 0.3, r: 0.9 + Math.random() * 1.3, color: col });
        }
        return out;
      },
      step: function (p, dt) { return EFFECTS.merge.step(p, dt); },
      draw: function (c, p) { return EFFECTS.merge.draw(c, p); }
    },
    merge: {
      spawn: function (o) {
        var out = [], col = o.color || '#e8c674';
        var from = Array.isArray(o.from) ? o.from : [];
        var flight = o.quick ? 0.22 : 0.4;
        for (var s = 0; s < from.length && s < 6; s++) {
          var sx = +from[s].x || 0, sy = +from[s].y || 0, dx = o.x - sx, dy = o.y - sy;
          var bow = Math.max(10, Math.min(60, Math.sqrt(dx * dx + dy * dy) * 0.18)) * (s % 2 ? -1 : 1);
          out.push({ comet: 1, x: sx, y: sy, px: sx, py: sy, x0: sx, y0: sy, dx: dx, dy: dy, bow: bow,
                     life: -0.03 * s, ttl: flight, color: col, r: o.big ? 3.4 : 2.6 });
          for (var i = 0; i < (o.quick ? 2 : 5); i++) {
            out.push({ x: sx, y: sy, px: sx, py: sy,
                       vx: (Math.random() - 0.5) * 60, vy: (Math.random() - 0.5) * 60 - 20,
                       life: -0.03 * s, ttl: 0.3 + Math.random() * 0.2, r: 0.9 + Math.random(), color: col });
          }
        }
        out.push({ ring: 1, x: o.x, y: o.y, r: 4, vr: o.big ? 520 : 360, life: -flight,
                   ttl: o.big ? 0.55 : 0.42, w: o.big ? 3.2 : 2.2, color: col });
        if (!o.quick) {
          out.push({ ring: 1, x: o.x, y: o.y, r: 2, vr: 220, life: -flight - 0.06, ttl: 0.5, w: 1.4, color: '#fff6d8' });
          var n = o.big ? 22 : 14;
          for (var k = 0; k < n; k++) {
            var a = Math.PI * 2 * k / n + Math.random() * 0.4, sp = 60 + Math.random() * 90;
            out.push({ x: o.x, y: o.y, px: o.x, py: o.y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp - 40,
                       life: -flight, ttl: 0.5 + Math.random() * 0.3, r: 1.2 + Math.random() * 1.4, color: col, spark: 1 });
          }
        }
        return out;
      },
      step: function (p, dt) {
        p.life += dt;
        if (p.life < 0) return true;                 /* still waiting its turn */
        if (p.ring) { p.r += p.vr * dt; p.vr *= (1 - 2.8 * dt); return p.life < p.ttl; }
        if (p.comet) {
          var k = Math.min(1, p.life / p.ttl);
          var e = 1 - Math.pow(1 - k, 3);            /* ease out, so it lands soft */
          p.px = p.x; p.py = p.y;
          p.x = p.x0 + p.dx * e;
          p.y = p.y0 + p.dy * e - Math.sin(e * Math.PI) * p.bow;
          return p.life < p.ttl;
        }
        p.px = p.x; p.py = p.y;
        p.x += p.vx * dt; p.y += p.vy * dt;
        p.vx *= (1 - 3 * dt); p.vy = p.vy * (1 - 3 * dt) + (p.spark ? 140 : 90) * dt;
        return p.life < p.ttl;
      },
      draw: function (c, p) {
        if (p.life < 0) return;
        var k = Math.max(0, 1 - p.life / p.ttl);
        c.lineCap = 'round';
        if (p.ring) {
          c.globalAlpha = k * 0.85;
          c.strokeStyle = p.color; c.lineWidth = p.w * k + 0.3;
          c.beginPath(); c.arc(p.x, p.y, p.r, 0, 6.2832); c.stroke();
          return;
        }
        c.globalAlpha = p.comet ? Math.min(1, 0.4 + k) : k;
        c.strokeStyle = p.color; c.lineWidth = p.r * (p.comet ? 1 : k) + 0.3;
        c.beginPath(); c.moveTo(p.px, p.py); c.lineTo(p.x, p.y); c.stroke();
        if (p.comet) {
          c.globalAlpha = k;
          c.fillStyle = p.color;
          c.beginPath(); c.arc(p.x, p.y, p.r * 0.9, 0, 6.2832); c.fill();
        }
      }
    },
    /* R456 'nat20' — the natural twenty. Jan: "rolling a 20 is very big in
       D&D, so make sure those rolls feel impactful, with particle effects
       and animations that go along with them."
       Deliberately the loudest thing in the file and deliberately RARE: two
       expanding rings, a starburst of long spokes, and a shower of embers
       that fall under gravity. It has to be unmistakable the first time a
       player sees it, because its whole job is to mark a moment.
       opts: x, y, color. */
    nat20: {
      spawn: function (o) {
        var out = [], col = o.color || '#e8c674', hot = '#fff6d8';
        out.push({ ring: 1, x: o.x, y: o.y, r: 6, vr: 520, life: 0, ttl: 0.62, w: 4, color: hot });
        out.push({ ring: 1, x: o.x, y: o.y, r: 3, vr: 330, life: -0.10, ttl: 0.66, w: 2.5, color: col });
        /* the spokes: a fixed star, so it reads as a shape and not as noise */
        for (var k = 0; k < 12; k++) {
          var sa = Math.PI * 2 * k / 12;
          out.push({ spoke: 1, x: o.x, y: o.y, a: sa,
                     r0: 10, len: 26 + (k % 2 ? 0 : 16),
                     life: 0, ttl: 0.4, color: hot });
        }
        for (var i = 0; i < 34; i++) {
          var a = Math.random() * 6.2832, sp = 120 + Math.random() * 260;
          out.push({
            x: o.x, y: o.y, px: o.x, py: o.y,
            vx: Math.cos(a) * sp, vy: Math.sin(a) * sp - 70,
            life: 0, ttl: 0.7 + Math.random() * 0.5,
            r: 1.4 + Math.random() * 2.2,
            color: Math.random() < 0.4 ? hot : col
          });
        }
        return out;
      },
      step: function (p, dt) {
        p.life += dt;
        if (p.life < 0) return true;
        if (p.ring) { p.r += p.vr * dt; p.vr *= (1 - 3.2 * dt); return p.life < p.ttl; }
        if (p.spoke) { p.r0 += 150 * dt; return p.life < p.ttl; }
        p.px = p.x; p.py = p.y;
        p.x += p.vx * dt; p.y += p.vy * dt;
        p.vx *= (1 - 2.2 * dt); p.vy = p.vy * (1 - 2.2 * dt) + 320 * dt;   /* embers fall */
        return p.life < p.ttl;
      },
      draw: function (c, p) {
        if (p.life < 0) return;
        var k = Math.max(0, 1 - p.life / p.ttl);
        if (p.ring) {
          c.globalAlpha = k * 0.9;
          c.strokeStyle = p.color; c.lineWidth = p.w * k + 0.5;
          c.beginPath(); c.arc(p.x, p.y, p.r, 0, 6.2832); c.stroke();
          return;
        }
        if (p.spoke) {
          c.globalAlpha = k * 0.95;
          c.strokeStyle = p.color; c.lineWidth = 2 * k + 0.4; c.lineCap = 'round';
          var ca = Math.cos(p.a), sa2 = Math.sin(p.a);
          c.beginPath();
          c.moveTo(p.x + ca * p.r0, p.y + sa2 * p.r0);
          c.lineTo(p.x + ca * (p.r0 + p.len * k), p.y + sa2 * (p.r0 + p.len * k));
          c.stroke();
          return;
        }
        c.globalAlpha = k;
        c.strokeStyle = p.color; c.lineWidth = p.r * k + 0.3; c.lineCap = 'round';
        c.beginPath(); c.moveTo(p.px, p.py); c.lineTo(p.x, p.y); c.stroke();
      }
    },
    /* R450b 'crown' — the crowning burst. A wide gold ring plus a fountain
       of sparks that rise and rain back down, twinkling out. opts: x, y. */
    crown: {
      spawn: function (o) {
        var out = [];
        out.push({ ring: 1, x: o.x, y: o.y, r: 8, vr: 300, life: 0, ttl: 0.6, w: 3, color: '#e8c674' });
        out.push({ ring: 1, x: o.x, y: o.y, r: 4, vr: 190, life: -0.12, ttl: 0.6, w: 2, color: '#f2e2b8' });
        for (var i = 0; i < 26; i++) {
          var a = -Math.PI / 2 + (Math.random() - 0.5) * 1.9;
          var sp = 120 + Math.random() * 160;
          out.push({
            x: o.x + (Math.random() - 0.5) * 24, y: o.y,
            vx: Math.cos(a) * sp * (0.4 + Math.random() * 0.6), vy: Math.sin(a) * sp,
            life: 0, ttl: 0.9 + Math.random() * 0.6,
            r: 1.3 + Math.random() * 1.7,
            tw: 4 + Math.random() * 7,
            color: Math.random() < 0.3 ? '#f2e2b8' : '#e8c674'
          });
        }
        return out;
      },
      step: function (p, dt) {
        p.life += dt;
        if (p.life < 0) return true;
        if (p.ring) { p.r += p.vr * dt; p.vr *= (1 - 3 * dt); return p.life < p.ttl; }
        p.x += p.vx * dt; p.y += p.vy * dt;
        p.vx *= (1 - 1.4 * dt); p.vy += 330 * dt;
        return p.life < p.ttl;
      },
      draw: function (c, p) {
        if (p.life < 0) return;
        var k = Math.max(0, 1 - p.life / p.ttl);
        if (p.ring) {
          c.globalAlpha = k * 0.9;
          c.strokeStyle = p.color; c.lineWidth = p.w * k + 0.5;
          c.beginPath(); c.arc(p.x, p.y, p.r, 0, 6.2832); c.stroke();
          return;
        }
        c.globalAlpha = k * (0.55 + 0.45 * Math.abs(Math.sin(p.life * p.tw)));
        c.fillStyle = p.color;
        c.beginPath(); c.arc(p.x, p.y, p.r, 0, 6.2832); c.fill();
      }
    },
    /* R450b 'motes' — a soft answer. Slow glowing motes drifting up from a
       choice that landed: event picks, draft picks, shop purchases, a
       companion joining. Gentler than sparks on purpose — a decision is a
       breath, not an explosion. opts: x, y, n, color, spread. */
    motes: {
      spawn: function (o) {
        var out = [], n = Math.max(3, Math.min(24, o.n | 0 || 10));
        for (var i = 0; i < n; i++) {
          out.push({
            x: o.x + (Math.random() - 0.5) * (o.spread || 90),
            y: o.y + (Math.random() - 0.5) * 16,
            vy: -(16 + Math.random() * 22),
            drift: (Math.random() - 0.5) * 22,
            life: -(Math.random() * 0.35), ttl: 1.5 + Math.random() * 0.9,
            r: 1.5 + Math.random() * 1.8,
            color: o.color || '#7ec4cf'
          });
        }
        return out;
      },
      step: function (p, dt) {
        p.life += dt;
        if (p.life < 0) return true;
        p.y += p.vy * dt;
        p.x += Math.sin(p.life * 2.6) * p.drift * dt;
        return p.life < p.ttl;
      },
      draw: function (c, p) {
        if (p.life < 0) return;
        var k = p.life / p.ttl;
        var a = k < 0.2 ? k / 0.2 : Math.max(0, 1 - (k - 0.2) / 0.8);
        c.fillStyle = p.color;
        c.globalAlpha = a * 0.28;
        c.beginPath(); c.arc(p.x, p.y, p.r * 2.4, 0, 6.2832); c.fill();
        c.globalAlpha = a * 0.85;
        c.beginPath(); c.arc(p.x, p.y, p.r, 0, 6.2832); c.fill();
      }
    },
    /* slow upward floaters (an ember shape) — not wired to anything yet.
       It exists so the ambient embers can migrate here in a future drop
       and so the registry ships with a second worked example. */
    rise: {
      spawn: function (o) {
        var out = [], n = Math.max(1, Math.min(40, o.n | 0 || 6));
        for (var i = 0; i < n; i++) {
          out.push({
            x: o.x + (Math.random() - 0.5) * (o.spread || 60),
            y: o.y,
            vy: -(12 + Math.random() * 26),
            drift: (Math.random() - 0.5) * 18,
            life: 0, ttl: 2.2 + Math.random() * 1.6,
            r: 1.2 + Math.random() * 1.2,
            color: o.color || '#e8934a'
          });
        }
        return out;
      },
      step: function (p, dt) {
        p.life += dt;
        p.y += p.vy * dt;
        p.x += Math.sin(p.life * 2.2) * p.drift * dt;
        return p.life < p.ttl;
      },
      draw: function (c, p) {
        var k = p.life / p.ttl;
        c.globalAlpha = k < 0.15 ? k / 0.15 : Math.max(0, 1 - (k - 0.15) / 0.85) * 0.8;
        c.fillStyle = p.color;
        c.beginPath();
        c.arc(p.x, p.y, p.r, 0, 6.2832);
        c.fill();
      }
    }
  };

  /* A shop choice expiring is a UI consequence, not a combat event, so this
     named cue deliberately stays outside PRESENTATION_RECIPES (whose ids must
     remain one-to-one with engine encounter metadata). The themed renderer
     still supplies the same hard per-emit cap and lifecycle guarantees. */
  EFFECTS.shop_timeout = themedFx({
    shape: 'shard', color: '#ef645d', hot: '#ffd2cd', n: 22, cap: 24,
    speed: 138, lift: 18, gravity: 230, ttl: 0.66, stagger: 0.025,
    size: 2.2, spread: 128, ringSpeed: 360, ringWidth: 3.2
  });

  /* Stable encounter presentation ids. Their names match engine profile
     metadata exactly, which lets a harness prove that content can never ship
     with a silent/missing effect. Low-level recipes are intentionally reused;
     the semantic id, palette, silhouette and motion remain distinct. */
  var PRESENTATION_RECIPES = {
    steel:            { shape: 'shard', color: '#c4b8d2', hot: '#f7f2ff', n: 10, speed: 92, gravity: 180, motion: 'shatter', staticCue: 'broken_shield' },
    alpha_ranger:     { shape: 'arrow', color: '#b46bff', hot: '#f1cf67', n: 12, cap: 12, speed: 210, lift: 0, gravity: 20, ttl: 0.42, stagger: 0.02, size: 2, spread: 34, orient: true, ringSpeed: 310, ringWidth: 2.8, ringTtl: 0.34, motion: 'sage_mark_arrow', staticCue: 'sage_mark_arrow_badge' },
    goblin_horn:      { shape: 'horn',  color: '#8fe0a5', hot: '#e8c674', n: 16, speed: 110, ringSpeed: 280, motion: 'call', staticCue: 'horn_pips' },
    nature_growth:    { shape: 'petal', color: '#7ee08f', hot: '#b3f5ee', n: 18, lift: 78, gravity: 24, ttl: 1.05, motion: 'rise', staticCue: 'growth_pips' },
    nature_vines:     { shape: 'vine',  color: '#66c77a', hot: '#d8ef9b', n: 18, cap: 18, speed: 44, lift: 48, gravity: 80, ttl: 0.58, stagger: 0.04, size: 3, spread: 520, ring: false, rail: true, motion: 'rail_bind', staticCue: 'vine_bind_badge' },
    /* R818: Jehn's roots keep their own identity (R756 copied nature_vines' names; the drawing is unchanged) */
    brown_roots:      { shape: 'vine',  color: '#b8885c', hot: '#f2d1a0', n: 24, cap: 24, speed: 44, lift: 48, gravity: 80, ttl: 0.62, stagger: 0.025, size: 3.5, spread: 520, ring: false, rail: true, motion: 'root_bind', staticCue: 'root_bind_badge' },
    legendary_horn:  { shape: 'horn',  color: '#f1cf67', hot: '#fff3bd', n: 22, speed: 128, ringSpeed: 350, ringWidth: 3, ttl: 0.72, motion: 'legendary_call', staticCue: 'legendary_horn_badge' },
    ragefast_enrage: { shape: 'shard', color: '#e74d42', hot: '#ffb06f', n: 28, speed: 174, lift: 58, gravity: 130, ringSpeed: 410, ringWidth: 3.4, ttl: 0.72, motion: 'rage_burst', staticCue: 'red_enrage_aura' },
    ragefast_strike: { shape: 'shard', color: '#ef4e43', hot: '#ffd0a3', n: 18, cap: 18, speed: 238, lift: 8, gravity: 85, spread: 38, ringSpeed: 340, ringWidth: 2.8, ttl: 0.46, motion: 'rage_slash', staticCue: 'red_d20_strike' },
    red_mark:         { shape: 'rune',  color: '#ef6f73', hot: '#ffd6d8', n: 8, speed: 62, ringWidth: 3, motion: 'lock_on', staticCue: 'target_reticle' },
    clockwork:        { shape: 'gear',  color: '#d6a94b', hot: '#b3f5ee', n: 16, spin: 6, gravity: 150, motion: 'rotate', staticCue: 'stance_badge' },
    clockwork_armor_heist:{ shape: 'gear', color: '#e0a05a', hot: '#fff0b5', n: 18, cap: 18, speed: 150, lift: 20, gravity: 120, spin: 7, ringSpeed: 340, ttl: 0.62, motion: 'armor_transfer', staticCue: 'armor_stolen_badge' },
    briar_ward_heist: { shape: 'vine', color: '#b46bff', hot: '#8fe0a5', n: 18, cap: 18, speed: 92, lift: 62, gravity: 45, ringSpeed: 310, ttl: 0.72, motion: 'ward_transfer', staticCue: 'ward_stolen_badge' },
    foundry_cannon:   { shape: 'shard', color: '#83e1e7', hot: '#fff0b5', n: 16, speed: 195, lift: 18, gravity: 95, ringSpeed: 290, ttl: 0.58, motion: 'cannon_burst', staticCue: 'cannon_badge' },
    foundry_explosion:{ shape: 'shard', color: '#f06b43', hot: '#fff0a8', n: 30, speed: 225, lift: 42, gravity: 245, ringSpeed: 500, ringWidth: 4, ttl: 0.68, motion: 'blast', staticCue: 'explosive_warning_badge' },
    foundry_repair:   { shape: 'gear',  color: '#79dce1', hot: '#f1cf67', n: 20, speed: 88, lift: 92, gravity: 22, spin: 8, ttl: 0.78, motion: 'repair_rise', staticCue: 'repair_armor_badge' },
    shell_split:      { shape: 'shard', color: '#b9a98e', hot: '#f1e6ca', n: 22, speed: 145, gravity: 260, motion: 'split', staticCue: 'nested_rings' },
    cursed_bloom:     { shape: 'petal', color: '#67c77c', hot: '#ef94ff', n: 24, lift: 95, gravity: 18, ttl: 1.25, ringSpeed: 180, motion: 'bloom', staticCue: 'flower_counter' },
    arcane_charge:    { shape: 'rune',  color: '#ef94ff', hot: '#b3f5ee', n: 28, speed: 132, spin: 5, ringSpeed: 360, ringWidth: 3.2, motion: 'charge', staticCue: 'charge_meter' },
    grave_bell:       { shape: 'rune',  color: '#8c6ba8', hot: '#e6d3f2', n: 18, speed: 74, gravity: 40, ttl: 1.1, motion: 'toll', staticCue: 'death_counter' },
    artificer_phase:  { shape: 'gear',  color: '#b3f5ee', hot: '#ef94ff', n: 28, spin: 8, ringSpeed: 400, ringWidth: 3, motion: 'phase_shift', staticCue: 'phase_badge' },
    ancient_grove:    { shape: 'petal', color: '#66b982', hot: '#e8c674', n: 30, lift: 108, gravity: 12, ttl: 1.35, motion: 'overgrow', staticCue: 'grove_counter' },
    grove_bloom:      { shape: 'petal', color: '#6fcf78', hot: '#f0a6e8', n: 24, lift: 116, gravity: 10, ttl: 0.9, motion: 'corrupt_bloom', staticCue: 'flower_wave_badge' },
    grove_treants:    { shape: 'vine',  color: '#708f4d', hot: '#c9e67d', n: 24, speed: 74, lift: 110, gravity: 28, ringSpeed: 300, ttl: 0.86, motion: 'roots_rise', staticCue: 'treant_wave_badge' },
    rune_ascension:   { shape: 'rune',  color: '#7de39a', hot: '#f1cf67', n: 28, speed: 118, lift: 128, gravity: 16, spin: 4, ringSpeed: 390, ringWidth: 3.5, ttl: 0.92, motion: 'runes_rise', staticCue: 'rune_giant_badge' },
    grove_siphon:     { shape: 'petal', color: '#74448f', hot: '#9fe7a2', n: 18, speed: 72, lift: 94, gravity: 8, ttl: 0.72, motion: 'life_siphon', staticCue: 'heal_armor_badge' },
    duelist_clock:    { shape: 'shard', color: '#d15a63', hot: '#f7df91', n: 20, speed: 158, gravity: 210, motion: 'lunge', staticCue: 'sword_pips' },
    duelist_beasts:   { shape: 'shard', color: '#b74b55', hot: '#f1cf67', n: 20, speed: 142, lift: 48, gravity: 195, ttl: 0.68, motion: 'beast_pounce', staticCue: 'beast_wave_badge' },
    duelist_lifesteal:{ shape: 'rune',  color: '#d34d69', hot: '#ffd8df', n: 18, speed: 88, lift: 76, gravity: 18, ringSpeed: 330, ringWidth: 3, ttl: 0.7, motion: 'blood_draw', staticCue: 'lifesteal_fangs_badge' },
    duelist_blood:    { shape: 'petal', color: '#a8264e', hot: '#ef839b', n: 16, speed: 82, lift: 88, gravity: 25, ttl: 0.66, motion: 'blood_heal', staticCue: 'beast_heal_badge' },
    duelist_blades:   { shape: 'shard', color: '#d75b68', hot: '#fff0c4', n: 24, speed: 205, lift: 20, gravity: 180, ringSpeed: 360, ttl: 0.62, motion: 'multi_slash', staticCue: 'multiattack_badge' },
    forge_overload:   { shape: 'gear',  color: '#e8934a', hot: '#fff0bf', n: 30, speed: 165, spin: 9, gravity: 250, ringSpeed: 440, motion: 'overload', staticCue: 'heat_meter' },
    rival_crest:      { shape: 'rune',  color: '#9f70d8', hot: '#e8c674', n: 22, speed: 118, ringWidth: 3, motion: 'crest', staticCue: 'rival_rule_badge' },
    crown_finale:     { shape: 'rune',  color: '#e8c674', hot: '#ef94ff', n: 36, cap: 40, speed: 178, spin: 6, ringSpeed: 520, ringWidth: 4, motion: 'finale', staticCue: 'crown_phase_badge' },
    /* R818 (campaign Step 0's real gaps): engine ids that drew only a static glow, each now its own motion and still, from
       the existing shapes. arcane_hex: the Hexbound Circle's weaken, violet runes settling in a slow ring */
    arcane_hex:       { shape: 'rune',  color: '#9a63f2', hot: '#e3d0ff', n: 20, cap: 20, speed: 64, lift: 18, gravity: 36, spin: 4, stagger: 0.05, ringSpeed: 250, ringWidth: 2.6, ttl: 0.82, motion: 'hex_circle', staticCue: 'hex_weaken_badge' },
    /* blizzard_melody: every Wynter moment, ice blue snow drifting up and across (her packet's Borrow is this cue) */
    blizzard_melody:  { shape: 'mote',  color: '#8fd0ff', hot: '#f4f8ff', n: 30, cap: 30, speed: 58, lift: 44, gravity: 10, stagger: 0.06, size: 2, spread: 140, ringSpeed: 210, ringWidth: 2, ttl: 1.05, motion: 'snow_drift', staticCue: 'frost_tint_badge' },
    /* charm_song: the Charmed Chorus's bench charm, rose petals lilting upward */
    charm_song:       { shape: 'petal', color: '#ff7aa8', hot: '#ffd6e8', n: 18, cap: 18, speed: 70, lift: 66, gravity: 12, spin: 3, ringSpeed: 240, ttl: 0.9, motion: 'charm_lilt', staticCue: 'charm_heart_badge' },
    /* red_marks_shroud: the Red Mark team shroud, dark crimson runes spread wide and settling (red_mark is the single lock-on) */
    red_marks_shroud: { shape: 'rune',  color: '#b8323c', hot: '#ffb3a8', n: 22, cap: 22, speed: 88, lift: 8, gravity: 64, spread: 170, ringSpeed: 300, ringWidth: 3.2, ttl: 0.7, motion: 'shroud_lock', staticCue: 'team_reticle_badge' },
    /* shell_repair: the Nesting Shells' heal and shield, pearl flakes rising with a sea green glint */
    shell_repair:     { shape: 'petal', color: '#e8dcc0', hot: '#9fe7c8', n: 18, cap: 18, speed: 60, lift: 84, gravity: 18, ringSpeed: 220, ttl: 0.86, motion: 'shell_mend', staticCue: 'shell_shield_badge' },
    /* R818 · Engine 124: Patches' Tonic's price (your Reserves skip their next volley), green and gold bubbles rising along the bench */
    tonic_price:      { shape: 'mote',  color: '#b5d86a', hot: '#ffe9a8', n: 16, cap: 16, speed: 40, lift: 52, gravity: -16, stagger: 0.04, size: 2.4, spread: 300, ring: false, rail: true, ttl: 0.78, motion: 'tonic_bubbles', staticCue: 'tonic_skip_badge' }
  };

  /* ============ R506 · ABILITY PRESENTATION BRIDGE ============
     The engine owns the published 440-row library plus versioned additions,
     and the generated manifest gives each one the stable id
     `ability.<ability id>`. Do not mirror those rows here. This bridge reads
     the engine metadata and composes one
     recipe from three small vocabularies: class palette, primary operation
     family and active/passive slot. The focused harness compares every
     derived descriptor to the checked-in generated manifest, so a new op or
     class cannot silently inherit the wrong visual language. */
  var ABILITY_MOTE_CAP = 18;
  var ABILITY_ID_RE = /^ability\.[a-z][a-z0-9_]{2,63}$/;
  var ABILITY_CLASS_PALETTES = {
    artificer: { color: '#79dce1', hot: '#ef94ff' },
    barbarian: { color: '#df654f', hot: '#ffbf72' },
    bard:      { color: '#e8c674', hot: '#ef94ff' },
    cleric:    { color: '#f0df9a', hot: '#b3f5ee' },
    druid:     { color: '#70c983', hot: '#d8ef9b' },
    fighter:   { color: '#b7becb', hot: '#f4e7bf' },
    monk:      { color: '#77d5c3', hot: '#f1df9c' },
    paladin:   { color: '#e1bc59', hot: '#b3f5ee' },
    ranger:    { color: '#77b978', hot: '#e8c674' },
    rogue:     { color: '#9a78d0', hot: '#ef94ff' },
    sorcerer:  { color: '#e06aaf', hot: '#b3f5ee' },
    warlock:   { color: '#9560b8', hot: '#e77478' },
    wizard:    { color: '#729de0', hot: '#d9c0ff' },
    universal: { color: '#ef94ff', hot: '#b3f5ee' }
  };
  /* Race-owned abilities keep their own readable color identity instead of
     borrowing the universal purple. The table is deliberately race-sized,
     not ability-sized: a future row names a race and automatically inherits
     its palette, while an unrecognized homebrew race stays on the restrained
     universal pair. Tiefling is live in the representative Engine 63 slice. */
  var ABILITY_RACE_PALETTES = {
    human:      { color: '#d8c8ad', hot: '#b3f5ee' },
    elf:        { color: '#91cfad', hot: '#e8c674' },
    half_elf:   { color: '#a8c9b6', hot: '#ef94ff' },
    high_elf:   { color: '#9ec8d8', hot: '#ef94ff' },
    dwarf:      { color: '#c79062', hot: '#f4dfaa' },
    halfling:   { color: '#8ecf91', hot: '#f1df9c' },
    gnome:      { color: '#79dce1', hot: '#ef94ff' },
    orc:        { color: '#86ad69', hot: '#e49368' },
    half_orc:   { color: '#91b975', hot: '#f0a06f' },
    goblin:     { color: '#78bd70', hot: '#e8c674' },
    tiefling:   { color: '#d15b86', hot: '#ef94ff' },
    aasimar:    { color: '#eadc9d', hot: '#b3f5ee' },
    dragonborn: { color: '#db735f', hot: '#ffd08a' },
    genasi:     { color: '#6ebed0', hot: '#ef94ff' },
    tabaxi:     { color: '#d7a65f', hot: '#b3f5ee' },
    firbolg:    { color: '#6fb68a', hot: '#d8ef9b' },
    goliath:    { color: '#aab1ba', hot: '#e8c674' },
    aarakocra:  { color: '#8fc9d8', hot: '#f3df9c' },
    /* R521: Dhampir and Fairy are 7th and 8th by live census (75 and 63
       characters) and both own three race abilities in the round 1 catalog.
       Without a pair here they wore the universal purple and read as
       class-less, which is the one thing a race ability must not do. */
    dhampir:    { color: '#a5566b', hot: '#e6d3f2' },
    fairy:      { color: '#8fd7c8', hot: '#ffd9f2' },
    universal:  { color: '#ef94ff', hot: '#b3f5ee' }
  };
  /* Same operation semantics used by the deterministic manifest generator.
     It is deliberately operation-sized, never ability-sized. Manifest parity
     makes additions fail loudly. */
  var ABILITY_OPERATION_FAMILY = {
    heal: 'mend', armor: 'armor', ward: 'spell_shield', barrier: 'barrier',
    edgeNext: 'edge', powNext: 'power', song: 'tempo', pin: 'control',
    pinClear: 'cleanse', strike: 'physical_impact', strikeM: 'arcane_impact',
    sharp: 'critical', critNext: 'critical', dodge: 'dodge', dodgeUp: 'dodge',
    stun: 'stun', spirit: 'summon', summon: 'summon', blast: 'blast',
    selfHp: 'sacrifice'
  };
  var ABILITY_FAMILY_MOTIFS = {
    mend:            { shape: 'petal', lift: 82, gravity: 18, motion: 'restore' },
    armor:           { shape: 'gear', gravity: 190, motion: 'brace' },
    spell_shield:    { shape: 'rune', spin: 3, gravity: 35, motion: 'ward' },
    barrier:         { shape: 'rune', spin: 2, gravity: 20, motion: 'barrier' },
    edge:            { shape: 'shard', gravity: 210, motion: 'whet' },
    power:           { shape: 'shard', lift: 58, gravity: 150, motion: 'charge' },
    tempo:           { shape: 'horn', lift: 66, gravity: 45, motion: 'rally' },
    control:         { shape: 'rune', spin: -3, gravity: 55, motion: 'bind' },
    cleanse:         { shape: 'petal', lift: 95, gravity: 8, motion: 'clear' },
    physical_impact: { shape: 'shard', gravity: 260, motion: 'impact' },
    arcane_impact:   { shape: 'rune', spin: 7, gravity: 70, motion: 'impact' },
    critical:        { shape: 'shard', gravity: 235, motion: 'critical' },
    dodge:           { shape: 'petal', lift: 48, gravity: 28, motion: 'slip' },
    stun:            { shape: 'gear', spin: 8, gravity: 175, motion: 'stun' },
    summon:          { shape: 'rune', spin: 4, lift: 72, gravity: 30, motion: 'summon' },
    blast:           { shape: 'rune', spin: 8, gravity: 215, motion: 'blast' },
    sacrifice:       { shape: 'shard', gravity: 285, motion: 'sacrifice' }
  };
  var ABILITY_TRIGGER_MOTION = {
    start: 'rise', first: 'slash', land: 'impact', round: 'pulse', cross: 'surge',
    ownerDown: 'fall_bloom', benchDown: 'bench_echo'
  };
  var ABILITY_RARITY_MOTES = [6, 8, 10, 12, 15, 18];
  /* ============================================================
     CLASS-CARD PRESENTATION REGISTRY

     A generated card is identified by its class, its stable printed name and
     the ordered operations the engine resolves.  The old card painter only
     knew those operations as a colour, which made a Warlock's called spirits
     look like the same generic rise as a Fighter's power buff.  This compact
     registry composes the existing class palettes with one semantic motif per
     operation.  A caller receives a complete cast/travel/impact recipe for
     every operation without mirroring the engine's forty-two card rows here.

     `CALL THE DEBT`, `DEPLOY TURRET` and `POISONED EDGE` need a stronger
     identity than their shared low-level operations provide, so only those
     ambiguous presentations carry name-specific overrides.  The mechanics
     remain entirely engine-owned. */
  var CLASS_CARD_MOTE_CAP = 19;
  var CLASS_CARD_RARITY_MOTES = [7, 8, 10, 12, 14, 16];
  var CLASS_CARD_REPEAT_CAPS = [19, 13, 9, 7];
  var CLASS_CARD_REPEAT_MS = [680, 340, 230, 160];
  var CLASS_CARD_OPERATION_VISUALS = {
    armor:      { family:'armor',       glyph:'gear',  template:'stamp',          motion:'brace',          target:'party_health', verb:'shield', staticCue:'armor_plate_badge' },
    barrier:    { family:'barrier',     glyph:'rune',  template:'halo',           motion:'seal',           target:'party_health', verb:'shield', staticCue:'barrier_badge' },
    bless:      { family:'blessing',    glyph:'rune',  template:'halo',           motion:'top_face',       target:'centre_die',   verb:'power',  staticCue:'perfect_roll_queue' },
    bonusDie:   { family:'bonus_dice',  glyph:'rune',  template:'chain',          motion:'dice_queue',     target:'owner_or_die', verb:'power',  staticCue:'bonus_dice_queue' },
    critNext:   { family:'critical',    glyph:'shard', template:'stamp',          motion:'whet',           target:'centre_die',   verb:'power',  staticCue:'critical_queue' },
    dodgeNext:  { family:'dodge',       glyph:'petal', template:'glitter_swirl',  motion:'afterimage',     target:'party_band',   verb:'power',  staticCue:'dodge_charge_badge' },
    dodgeUp:    { family:'dodge',       glyph:'petal', template:'halo',           motion:'wind_guard',     target:'party_band',   verb:'power',  staticCue:'dodge_chance_badge' },
    heal:       { family:'mend',        glyph:'heart', template:'rain',           motion:'restore',        target:'party_health', verb:'heal',   staticCue:'healing_badge' },
    lifesteal:  { family:'lifesteal',   glyph:'heart', template:'chain',          motion:'blood_return',   target:'centre_die',   verb:'heal',   staticCue:'lifesteal_fangs_badge' },
    pin:        { family:'weaken',      glyph:'rune',  template:'chain',          motion:'bind',           target:'enemy_health', verb:'bind',   staticCue:'weaken_badge' },
    powNext:    { family:'power',       glyph:'shard', template:'beam',           motion:'charge',         target:'centre_die',   verb:'power',  staticCue:'next_attack_power_badge' },
    rageAll:    { family:'rage',        glyph:'flame', template:'wave',           motion:'battle_cry',     target:'party_band',   verb:'power',  staticCue:'party_power_queue' },
    sharp:      { family:'critical',    glyph:'shard', template:'ring',           motion:'whet',           target:'party_band',   verb:'power',  staticCue:'critical_range_badge' },
    song:       { family:'song',        glyph:'note',  template:'wave',           motion:'refrain',        target:'party_band',   verb:'power',  staticCue:'song_bank_badge' },
    spirits:    { family:'summon',      glyph:'rune',  template:'door',           motion:'summon_strike',  target:'enemy_health', verb:'strike', staticCue:'summoned_strikes_badge' },
    stun:       { family:'stun',        glyph:'rune',  template:'stamp',          motion:'lock',           target:'enemy_health', verb:'bind',   staticCue:'stun_badge' },
    swingTwice: { family:'tempo',       glyph:'shard', template:'chain',          motion:'double_slash',   target:'centre_die',   verb:'power',  staticCue:'extra_attack_badge' },
    ward:       { family:'spell_shield',glyph:'rune',  template:'ring',           motion:'ward',           target:'party_health', verb:'shield', staticCue:'spell_shield_badge' }
  };
  var CLASS_CARD_NAME_VISUALS = {
    call_the_debt: {
      family:'debt', motion:'contract_break', staticCue:'debt_contract_badge', hot:'#e77478',
      effects: {
        powNext: { family:'debt_power', glyph:'seal', template:'contract_seal', motion:'debt_mark', staticCue:'debt_power_badge' },
        spirits: { family:'debt_spirits', glyph:'rune', template:'chain', motion:'spirits_collect', target:'enemy_health', verb:'strike', staticCue:'debt_spirits_badge' }
      }
    },
    deploy_turret: {
      family:'turret', motion:'machine_deploy', staticCue:'turret_badge',
      effects: {
        spirits: { family:'turret_volley', glyph:'gear', template:'beam', motion:'turret_fire', target:'enemy_health', verb:'strike', staticCue:'turret_queue_badge' }
      }
    },
    poisoned_edge: {
      family:'poison', motion:'venom_edge', staticCue:'poisoned_edge_badge', hot:'#dca8f2',
      effects: {
        pin: { family:'poison', glyph:'leaf', template:'chain', motion:'venom_bind', target:'enemy_health', verb:'bind', staticCue:'poison_weaken_badge' },
        bonusDie: { family:'poisoned_dice', glyph:'rune', template:'chain', motion:'venom_dice', target:'owner_or_die', verb:'power', staticCue:'poison_dice_queue' }
      }
    }
  };
  function classCardSlug(value) {
    return String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  }
  function classCardOps(card) {
    var out = [], rows = card && card.ops;
    if (!Array.isArray(rows)) return out;
    for (var i = 0; i < rows.length; i++) {
      var op = typeof rows[i] === 'string' ? rows[i] : rows[i] && rows[i].op;
      if (op) out.push(String(op));
    }
    return out;
  }
  function classCardVisualMerge(base, extra) {
    var out = {}, key;
    for (key in base) if (own(base, key)) out[key] = base[key];
    for (key in extra) if (own(extra, key) && key !== 'effects') out[key] = extra[key];
    return out;
  }
  function classCardPresentation(card, meta) {
    card = card || {}; meta = meta || {};
    var name = String(card.name || meta.name || '').trim();
    var slug = classCardSlug(name);
    var ops = classCardOps(card);
    if (!slug || !ops.length) return null;
    var rawClass = String(meta.cls || meta.classId || card.cls || card.classId || 'universal');
    var classId = classCardSlug(rawClass === '_' ? 'universal' : rawClass) || 'universal';
    if (!own(ABILITY_CLASS_PALETTES, classId)) classId = 'universal';
    var palette = ABILITY_CLASS_PALETTES[classId];
    var rarity = Math.max(0, Math.min(5, meta.rar != null ? (meta.rar | 0) :
      (meta.rarity != null ? (meta.rarity | 0) : 0)));
    var signature = !!(meta.sig || meta.signature);
    var repeat = Math.max(0, meta.repeat != null ? (meta.repeat | 0) : 0);
    var band = Math.min(CLASS_CARD_REPEAT_CAPS.length - 1, repeat);
    var cap = CLASS_CARD_REPEAT_CAPS[band];
    var duration = CLASS_CARD_REPEAT_MS[band];
    var named = CLASS_CARD_NAME_VISUALS[slug] || {};
    var color = palette.color, hot = named.hot || (signature ? '#f1cf67' : palette.hot);
    var effects = [];
    for (var i = 0; i < ops.length; i++) {
      var op = ops[i], base = CLASS_CARD_OPERATION_VISUALS[op];
      if (!base) return null;
      var override = named.effects && named.effects[op] || {};
      var visual = classCardVisualMerge(base, override);
      var particles = Math.max(3, Math.min(cap - 1,
        Math.round((CLASS_CARD_RARITY_MOTES[rarity] + (signature ? 2 : 0)) *
          (band === 0 ? 1 : band === 1 ? 0.68 : band === 2 ? 0.48 : 0.36))));
      var effectId = 'classcard.' + classId + '.' + slug + '.' + classCardSlug(op) + '.' + i + '.b' + band;
      var recipe = {
        id: effectId, template: visual.template, glyph: visual.glyph,
        color: color, hot: hot, particles: particles,
        max_particles: Math.max(3, cap - 1), duration_ms: duration,
        reduced_motion: visual.staticCue
      };
      effects.push({
        id: effectId, op: op, family: visual.family, motion: visual.motion,
        sourceCue: 'card_or_caster', targetCue: visual.target,
        impactVerb: visual.verb, staticCue: visual.staticCue,
        color: color, hot: hot, durationMs: duration,
        defaultParticles: particles + 1, maxParticles: cap, recipe: recipe
      });
    }
    return {
      id: 'classcard.' + classId + '.' + slug, name: name, classId: classId,
      palette: 'class.' + classId, family: named.family || effects[0].family,
      motion: named.motion || effects[0].motion,
      staticCue: named.staticCue || ('class_card_' + slug + '_cue'),
      color: color, hot: hot, rarity: rarity, signature: signature,
      repeat: repeat, repeatBand: band, durationMs: duration,
      operations: ops.slice(), effects: effects
    };
  }
  function classCardPresentationIds() {
    var engine = null, table = null, out = [];
    try { engine = window.HOHContract || null; table = engine && engine.CLASS_CARDS; } catch (e) { table = null; }
    if (!table) return out;
    Object.keys(table).forEach(function (owner) {
      (table[owner] || []).forEach(function (base, variant) {
        var def = base;
        try { if (engine.classCardOf) def = engine.classCardOf(owner, variant, engine.ENGINE_VERSION); } catch (e) {}
        var descriptor = classCardPresentation(def, { cls: owner });
        if (descriptor) out.push(descriptor.id);
      });
    });
    return out.sort();
  }
  function classCardOperationIds() { return Object.keys(CLASS_CARD_OPERATION_VISUALS).sort(); }
  var abilityEffectCache = {};
  function abilityTable() {
    try { return (window.HOHContract && window.HOHContract.ABILITIES) || {}; }
    catch (_) { return {}; }
  }
  function abilitySlug(value) {
    return String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  }
  function abilityIdFromPresentation(name) {
    name = String(name || '');
    return ABILITY_ID_RE.test(name) ? name.slice(8) : '';
  }
  function abilityPrimaryOp(row, hint) {
    var ops = row && row.ops;
    if (!ops && hint) ops = hint.operations || hint.ops;
    if (!ops || !ops.length) return '';
    /* A result event may ask for the motif of its own operation in a compound
       ability. Accept that hint only when the canonical row really owns the
       operation; an event cannot forge a stronger or different visual family. */
    var wanted = hint && String(hint.operation || hint.op || '');
    if (row && wanted) {
      for (var i = 0; i < ops.length; i++) {
        var item = ops[i];
        var itemId = typeof item === 'string' ? item :
          (Object.prototype.toString.call(item) === '[object Array]' ? item[0] : item && item.id) || '';
        if (String(itemId) === wanted) return wanted;
      }
    }
    var first = ops[0];
    return typeof first === 'string' ? first :
      (Object.prototype.toString.call(first) === '[object Array]' ? first[0] : first && first.id) || '';
  }
  function abilityMeta(name, hint) {
    var id = abilityIdFromPresentation(name);
    if (!id) return null;
    var table = abilityTable();
    var row = own(table, id) ? table[id] : null;
    hint = hint || {};
    /* If this client has never seen the id, it must not accept an event's
       claimed class/rarity as presentation authority. A future engine row is
       picked up automatically; a genuinely unknown id gets the calm universal
       fallback until that row arrives. */
    var cls = row ? row.c : '*';
    var classKey = cls === '*' ? 'universal' : abilitySlug(cls);
    if (!own(ABILITY_CLASS_PALETTES, classKey)) classKey = 'universal';
    var raceKey = '';
    if (row && row.o === 'race' && row.race) {
      raceKey = abilitySlug(row.race);
      if (!own(ABILITY_RACE_PALETTES, raceKey)) raceKey = 'universal';
    }
    var op = abilityPrimaryOp(row, row ? hint : null);
    var family = ABILITY_OPERATION_FAMILY[op] || 'power';
    var slotRaw = row ? row.p : 'active';
    var slot = slotRaw === 'pas' || slotRaw === 'passive' ? 'passive' : 'active';
    var rarityRaw = row ? row.r : 0;
    var rarity = Math.max(0, Math.min(5, finiteNumber(rarityRaw, 0) | 0));
    var trigger = row ? row.tr : 'start';
    if (trigger && typeof trigger === 'object') trigger = trigger.id;
    var triggerShort = trigger === 'fight_start' ? 'start' :
      trigger === 'owner_first_action' ? 'first' :
      trigger === 'owner_landed_attack' ? 'land' :
      trigger === 'round_start' ? 'round' :
      trigger === 'friendly_shared_health_crosses_below' ? 'cross' :
      trigger === 'owner_death' ? 'ownerDown' :
      trigger === 'friendly_bench_death' ? 'benchDown' : trigger;
    return { id: id, presentationId: 'ability.' + id, classKey: classKey,
      raceKey: raceKey, palette: raceKey ? ('race.' + raceKey) : ('class.' + classKey),
      family: family, operation: op, slot: slot, rarity: rarity, trigger: triggerShort,
      motion: ABILITY_TRIGGER_MOTION[triggerShort] || 'rise', known: !!row };
  }
  function abilityRecipe(meta) {
    var palette = meta.raceKey ? (ABILITY_RACE_PALETTES[meta.raceKey] || ABILITY_RACE_PALETTES.universal) :
      (ABILITY_CLASS_PALETTES[meta.classKey] || ABILITY_CLASS_PALETTES.universal);
    var motif = ABILITY_FAMILY_MOTIFS[meta.family] || ABILITY_FAMILY_MOTIFS.power;
    var active = meta.slot === 'active';
    var ownerDown = meta.trigger === 'ownerDown', benchDown = meta.trigger === 'benchDown';
    return {
      shape: motif.shape, color: palette.color, hot: palette.hot,
      n: ABILITY_RARITY_MOTES[meta.rarity], cap: ABILITY_MOTE_CAP,
      speed: (active ? 105 : 78) + meta.rarity * (active ? 7 : 5),
      spread: 54 + meta.rarity * 7, size: 1.65 + meta.rarity * 0.12,
      lift: ownerDown ? Math.max(92, motif.lift || 0) : benchDown ? Math.max(54, motif.lift || 0) :
        (motif.lift == null ? (active ? 28 : 58) : motif.lift),
      gravity: ownerDown ? Math.min(42, motif.gravity == null ? 42 : motif.gravity) :
        (motif.gravity == null ? (active ? 155 : 65) : motif.gravity),
      spin: motif.spin || 0, ttl: ownerDown ? 0.92 : (benchDown ? 0.72 : (active ? 0.62 : 0.82)),
      ring: true, ringSpeed: (ownerDown ? 330 : benchDown ? 220 : (active ? 275 : 175)) + meta.rarity * 18,
      ringWidth: active ? 2.6 : 1.7,
      motion: meta.motion + '_' + motif.motion,
      staticCue: (ownerDown ? 'owner_down' : benchDown ? 'bench_down' : meta.slot) + '_' + meta.family + '_cue'
    };
  }
  function abilityPresentationDescriptor(name, hint) {
    var meta = abilityMeta(name, hint);
    if (!meta) return null;
    var recipe = abilityRecipe(meta);
    return {
      id: meta.presentationId, abilityId: meta.id, classId: meta.classKey,
      raceId: meta.raceKey || null, palette: meta.palette, family: meta.family,
      operation: meta.operation, trigger: meta.trigger, slot: meta.slot,
      rarity: meta.rarity, shape: recipe.shape, color: recipe.color, hot: recipe.hot,
      motion: meta.motion, staticCue: recipe.staticCue,
      sourceCue: meta.trigger === 'ownerDown' ? 'death_active' :
        meta.trigger === 'benchDown' ? 'bench_death_passive' :
        (meta.slot === 'active' ? 'active_cast' : 'passive_wake'),
      targetCue: meta.family + '_state_cue',
      defaultParticles: Math.min(ABILITY_MOTE_CAP, recipe.n) + 1,
      maxParticles: ABILITY_MOTE_CAP + 1,
      known: meta.known
    };
  }
  function abilityPresentationIds() {
    return Object.keys(abilityTable()).sort().map(function (id) { return 'ability.' + id; });
  }
  function abilityEffect(name, hint) {
    /* Compound rows reuse one presentation id for several result operations.
       Cache by canonical operation as well as id so a heal result cannot leave
       the following ward result wearing its petal recipe. */
    var cacheOp = hint && String(hint.operation || hint.op || '');
    var cacheKey = name + '|' + cacheOp;
    if (own(abilityEffectCache, cacheKey)) return abilityEffectCache[cacheKey];
    var meta = abilityMeta(name, hint); if (!meta) return null;
    var fx = themedFx(abilityRecipe(meta));
    /* Cache canonical rows so repeated round triggers allocate no closures.
       Do not cache a universal fallback: the FX asset can load before the
       engine, and an early probe must not permanently hide metadata that
       becomes available later in the same page lifecycle. */
    if (meta.known) abilityEffectCache[cacheKey] = fx;
    return fx;
  }
  /* ============ R523 · THE CATALOG RENDERER (THE FX LAW's battle half) ====
     Every row of the 469-ability catalog carries a CONCRETE fx_recipe:
     one of fourteen base templates composed with a sub-variant, its own
     colour pair, a glyph, anchors, timing and a particle count, so every
     ability LOOKS distinct without hand-building hundreds of animations.
     This renderer turns any such recipe into one bounded particle program.

     The recipe arrives WITH the emit (Phase 4 ships recipes beside the
     registry rows; nothing here reads the catalog JSON), so the door is:

       window.hohFx.emit('catalog.fx', { recipe: row.fx_recipe,
         rarity,          0-5, scales every per-particle dimension (R638)
         x, y,            the origin anchor's centre
         tx, ty })        the target anchor's centre (defaults to origin)

     Fourteen templates, five motion primitives underneath (radial, fall,
     orbit, line, plate), every one capped at CATALOG_MOTE_CAP particles and
     under two seconds, because an ability effect decorates a beat and must
     never own the fight. Reduced motion draws nothing here — the row's own
     reduced_motion sentence is the contract, surfaced by the caller. */
  var CATALOG_MOTE_CAP = 40;
  var CATALOG_TEMPLATES = {
    burst: 1, rain: 1, ring: 1, chain: 1, beam: 1, wave: 1, stamp: 1,
    halo: 1, door: 1, portrait_door: 1, contract_seal: 1, stone_crack: 1,
    flame_jet: 1, glitter_swirl: 1
  };
  function catalogGlyphDraw(c, p) {
    var g = p.glyph || 'mote', r = p.r;
    c.save(); c.translate(p.x, p.y); c.rotate(p.a || 0);
    /* ⛔ R692 · `gear` DREW A SQUARE. 149 recipes ask for a gear and this drew
       `rect(-r,-r,2r,2r)`, so nearly a quarter of the catalog threw plain
       boxes. Six teeth on a hub is still cheap at 2-8px and actually reads as
       machinery, which is what the Artificer and Clockwork families are. */
    if (g === 'gear') {
      c.beginPath();
      for (var _gt = 0; _gt < 6; _gt++) {
        var _ga = _gt * 1.0472, _gb = _ga + 0.52;
        c.lineTo(Math.cos(_ga) * r * 1.35, Math.sin(_ga) * r * 1.35);
        c.lineTo(Math.cos(_gb) * r * 0.72, Math.sin(_gb) * r * 0.72);
      }
      c.closePath(); c.stroke();
    }
    /* ⛔ AND `horn` HAD NO CASE AT ALL. Nine recipes name it and every one
       fell through to the generic circle, so they were indistinguishable from
       a mote. A curved taper reads as a horn even at three pixels. */
    else if (g === 'horn') {
      c.beginPath();
      c.moveTo(-r * 1.2, r * 0.7);
      c.quadraticCurveTo(r * 0.2, r * 0.9, r * 1.3, -r * 1.1);
      c.quadraticCurveTo(r * 0.1, -r * 0.1, -r * 1.2, r * 0.7);
      c.closePath(); c.fill();
    }
    else if (g === 'shard' || g === 'stone') { c.beginPath(); c.moveTo(0, -r * 1.5); c.lineTo(r, r); c.lineTo(-r, r); c.closePath(); c.fill(); }
    else if (g === 'rune' || g === 'seal') { c.beginPath(); c.moveTo(-r, 0); c.lineTo(0, -r); c.lineTo(r, 0); c.lineTo(0, r); c.closePath(); c.stroke(); }
    else if (g === 'petal' || g === 'leaf') { c.beginPath(); c.ellipse(0, 0, r * 0.7, r * 1.6, 0, 0, 6.2832); c.fill(); }
    else if (g === 'note') { c.beginPath(); c.arc(0, r * 0.4, r * 0.7, 0, 6.2832); c.fill(); c.beginPath(); c.moveTo(r * 0.6, r * 0.2); c.lineTo(r * 0.6, -r * 1.3); c.stroke(); }
    else if (g === 'flame') { c.beginPath(); c.moveTo(0, -r * 1.6); c.quadraticCurveTo(r, 0, 0, r); c.quadraticCurveTo(-r, 0, 0, -r * 1.6); c.fill(); }
    else if (g === 'heart') { c.beginPath(); c.moveTo(0,r); c.bezierCurveTo(-r*2,-r,-r,-r*2,0,-r*.5); c.bezierCurveTo(r,-r*2,r*2,-r,0,r); c.fill(); }
    else if (g === 'crown') { c.beginPath(); c.moveTo(-r,r); c.lineTo(-r*1.4,-r); c.lineTo(-r*.4,0); c.lineTo(0,-r*1.5); c.lineTo(r*.4,0); c.lineTo(r*1.4,-r); c.lineTo(r,r); c.closePath(); c.fill(); }
    else { c.beginPath(); c.arc(0, 0, r, 0, 6.2832); c.fill(); }
    c.restore();
  }
  function catalogFx(recipe, rarity) {
    recipe = recipe || {};
    var template = String(recipe.template || 'burst');
    if (!own(CATALOG_TEMPLATES, template)) return null;
    /* ⛔ R638: RARITY SCALED THE COUNT AND NOTHING ELSE. After R628 fixed the
       count a Mythic threw 17 motes where a Common threw 6, but every mote was
       the same radius, the same speed and the same spread, and the ring was
       identical. On a 375px board three times as many identical specks reads
       as the same animation at a higher density, not as a bigger event, and
       "more impactful" is Jan's word for the second thing.
       So one factor scales every per-particle DIMENSION alongside the count:
       radius, launch speed, spread, orbit radius and the ring. A Common is
       unchanged at 1.0 by construction, so nothing below the top of the ladder
       gets quietly louder, and a Mythic is 45% bigger and faster on top of
       being three times as many. The rarity arrives beside the recipe rather
       than on it, because the recipe object is the shared table entry and
       R626 was paid for handing out a live handle to one of those. */
    var _rk = Math.max(0, Math.min(5, rarity | 0));
    var k = 1 + _rk * 0.09;
    var dur = Math.max(0.25, Math.min(2, finiteNumber(recipe.duration_ms, 700) / 1000));
    /* ⛔ R628 · THE COUNT IS `particles`. THE CEILING IS `max_particles`.
       This read them the other way round, and every generated recipe writes
       max_particles as a CONSTANT (build-ability-registry.cjs:460 sets
       CAT.MOTE_CAP + 1 for every row at every rarity), so all 669 compiled
       abilities drew the same ~19 motes whether they were common or mythic.
       The generator's rarity ramp — 5.9 motes for a common, 17.4 for a
       mythic — was computed correctly into the one field nobody read.
       Same genre as R521's four dead class cards and R619's inert extraBeat:
       a value written to a field no reader consumes, invisible because
       nothing errors and the effect still plays.
       MEASURED on a real canvas in harness/browser/abilityfx.cjs, all 669
       fired: flat 19.26 at every rarity band before this line, 5.9 -> 17.4
       after, against a pause ladder that already ran 260ms -> 1100ms. Jan's
       rule is that a rarer ability lands harder, and now both levers carry it.
       The fallbacks are unchanged: a recipe carrying only max_particles still
       uses it as the count, and one carrying neither still lands on 14. */
    var _mcap = Math.min(CATALOG_MOTE_CAP, finiteNumber(recipe.max_particles, CATALOG_MOTE_CAP));
    var count = Math.max(3, Math.min(_mcap,
      finiteNumber(recipe.particles, finiteNumber(recipe.max_particles, 14)) | 0));
    /* ⛔ R692 · THE `sub` WAS AUTHORED 50 WAYS AND READ ZERO TIMES.
       build-ability-catalog.cjs gives every template four subs precisely so
       "fifty-five heal rows do not all look like one rain", and this painter
       referenced `recipe.sub` nowhere at all. 1,611 abilities with 1,611
       unique colours collapsed to about eleven motions you could tell apart.
       The sub now shapes the SPAWN — spread, speed, stagger, radius, spin —
       inside the same free / orbit / line / ring / plate modes the step and
       draw functions already understand. No new physics, no new fields. */
    var sub = String(recipe.sub || '');
    /* one small deterministic table, read by the branches below */
    var SUBFX = {
      /* travelling */
      beam_line:   { w: 1.0, stag: 1.0, spread: 0 },
      beam_lance:  { w: 1.9, stag: 0.15, spread: 0 },
      beam_thread: { w: 0.5, stag: 1.6, spread: 0 },
      beam_split:  { w: 1.1, stag: 0.6,  spread: 26 },
      chain_link:  { w: 1.0, stag: 1.0, spread: 0 },
      chain_snap:  { w: 1.4, stag: 0.2, spread: 0 },
      chain_wrap:  { w: 0.9, stag: 1.0, spread: 18 },
      chain_drag:  { w: 1.2, stag: 1.9, spread: 6 },
      /* radial */
      burst_ring:   { sp: 1.0, g: 1.0, stag: 1.0 },
      burst_shard:  { sp: 1.45, g: 1.5, stag: 0.4 },
      burst_double: { sp: 1.15, g: 0.8, stag: 1.0, twin: 1 },
      burst_late:   { sp: 0.8, g: 0.6, stag: 2.2 },
      /* falling */
      rain_soft:    { sp: 0.8, band: 1.0, stag: 1.4 },
      rain_sheet:   { sp: 1.0, band: 1.7, stag: 0.5 },
      rain_updraft: { sp: -0.7, band: 1.0, stag: 1.0 },
      rain_single:  { sp: 1.5, band: 0.25, stag: 0.3 },
      /* orbiting */
      ring_wide:  { rr: 1.5, va: 0.8 },
      ring_tight: { rr: 0.6, va: 1.7 },
      ring_pair:  { rr: 1.0, va: 1.0, twin: 1 },
      ring_pulse: { rr: 1.0, va: 1.0, shrink: 22 },
      stamp_seal: { rr: 1.0, va: 1.0 },
      stamp_stack:{ rr: 0.7, va: 0.6, stack: 1 },
      stamp_lock: { rr: 1.25, va: -1.3, shrink: 26 }
    };
    var SF = SUBFX[sub] || {};
    var col = typeof recipe.color === 'string' ? recipe.color : '#e8c674';
    var hot = typeof recipe.hot === 'string' ? recipe.hot : '#fff6d8';
    var glyph = String(recipe.glyph || 'mote');
    return {
      spawn: function (o) {
        o = o || {};
        var x = finiteNumber(o.x, viewportMiddle('x'));
        var y = finiteNumber(o.y, viewportMiddle('y'));
        var tx = finiteNumber(o.tx, x), ty = finiteNumber(o.ty, y);
        var out = [], i, a, sp;
        var ringOn = o.hold ? _rk >= 2 : recipe.ring !== false;
        if (ringOn) out.push({ mode: 'ring', x: template === 'beam' || template === 'chain' ? tx : x,
          y: template === 'beam' || template === 'chain' ? ty : y,
          r: 6 * k, vr: 240 * k, life: 0, ttl: Math.min(0.5, dur), w: 2.2 * k, color: col });
        for (i = 0; i < count; i++) {
          var isHot = (i % 4) === 0;
          var p = { glyph: glyph, color: isHot ? hot : col, life: -(i / count) * 0.18,
                    ttl: dur * (0.7 + (i % 5) * 0.08), r: (1.7 + (i % 3)) * k, a: 0, spin: 0 };
          if (template === 'burst' || template === 'stone_crack') {
            a = 6.2832 * i / count;
            /* burst_double throws two opposed fans instead of one even ring */
            if (SF.twin) a = 6.2832 * ((i % 2) ? i : count - i) / count;
            sp = (template === 'stone_crack' ? 120 : 92) * k * (SF.sp || 1);
            p.mode = 'free'; p.x = x; p.y = y;
            p.vx = Math.cos(a) * sp; p.vy = Math.sin(a) * sp - (template === 'stone_crack' ? 10 : 40);
            p.g = (template === 'stone_crack' ? 320 : 90) * (SF.g || 1); p.spin = 3;
            if (SF.stag) p.life = -(i / count) * 0.18 * SF.stag;
          } else if (template === 'rain') {
            p.mode = 'free';
            p.x = tx + ((i / count) - 0.5) * 110 * k * (SF.band == null ? 1 : SF.band);
            p.y = ty - 70 * k - (i % 4) * 12;
            p.vx = 0; p.vy = (120 + (i % 5) * 22) * k * (SF.sp || 1); p.g = 60;
            if (SF.stag) p.life = -(i / count) * 0.18 * SF.stag;
          } else if (template === 'ring' || template === 'halo') {
            p.mode = 'orbit'; p.cx = x; p.cy = template === 'halo' ? y - 26 : y;
            p.rr = (template === 'halo' ? 22 : 14 + (i % 3) * 8) * k * (SF.rr || 1);
            p.a0 = 6.2832 * (SF.twin ? (i % 2 ? i : i + count / 2) : i) / count;
            p.va = (template === 'halo' ? 2.2 : 3.4) * (SF.va || 1);
            p.vy = template === 'halo' ? -16 : 0;
            if (SF.shrink) p.shrink = SF.shrink;
          } else if (template === 'chain' || template === 'beam') {
            p.mode = 'line'; p.x0 = x; p.y0 = y;
            /* beam_split and chain_wrap fan the endpoint so the line forks */
            var _sp = SF.spread ? ((i % 2 ? 1 : -1) * SF.spread * k) : 0;
            p.dx = (tx - x) + _sp; p.dy = (ty - y) - Math.abs(_sp) * 0.35;
            p.k0 = (i / count) * (SF.stag == null ? 1 : SF.stag);
            p.vk = 1 / Math.max(0.25, dur * 0.7);
            p.x = x; p.y = y; p.w = (template === 'beam' ? 2.6 : 1.6) * k * (SF.w || 1);
          } else if (template === 'wave') {
            p.mode = 'free'; p.x = x - 70 * k + ((i / count) * 140 * k); p.y = y + ((i % 5) - 2) * 7;
            p.vx = 150 * k; p.vy = 0; p.g = 0;
          } else if (template === 'portrait_door') {
            /* ⛔ R692 · THIS WAS BYTE-IDENTICAL TO `door`. Every ternary in the
               old shared branch tested `door || portrait_door`, so the two
               names drew the same thing and one of them was decoration. A
               portrait door opens TOWARD the tile it names: the ring starts
               wide and closes inward, and drifts down onto the body rather
               than up and away, which is what "someone arrives here" looks
               like next to a plain door's exit. */
            p.mode = 'orbit'; p.cx = tx; p.cy = ty;
            p.rr = (30 - (i % 3) * 3) * k;
            p.a0 = 6.2832 * i / count;
            p.va = 1.9; p.shrink = 30; p.vy = 16;
          } else if (template === 'stamp' || template === 'contract_seal' || template === 'door') {
            p.mode = 'orbit'; p.cx = template === 'door' ? tx : x;
            p.cy = template === 'door' ? ty : y;
            p.rr = ((template === 'contract_seal' ? 26 : 18) - (i % 3) * 4) * k * (SF.rr || 1);
            p.a0 = 6.2832 * i / count;
            p.va = (template === 'contract_seal' ? -2.6 : 1.4) * (SF.va || 1);
            p.shrink = SF.shrink != null ? SF.shrink
              : (template === 'stamp' || template === 'contract_seal' ? 14 : 0);
            p.vy = template === 'door' ? -34 : 0;
            /* stamp_stack lands its motes in a column instead of a circle */
            if (SF.stack) { p.rr = 8 * k; p.cy = y - (i % 4) * 7 * k; }
          } else if (template === 'flame_jet') {
            a = -1.5708 + ((i / count) - 0.5) * 0.9;
            p.mode = 'free'; p.x = x + ((i % 3) - 1) * 4; p.y = y;
            p.vx = Math.cos(a) * 90 * k; p.vy = Math.sin(a) * 150 * k; p.g = -40; p.spin = 2;
          } else { /* glitter_swirl */
            p.mode = 'orbit'; p.cx = x; p.cy = y; p.rr = 6 + (i % 6) * 5;
            p.a0 = 6.2832 * i / count; p.va = 4.4; p.vy = -22;
          }
          out.push(p);
        }
        /* R669: every particle shares the firing's rendered clock. Cached
           recipes stay immutable; the span belongs to this particular emit. */
        if (o.span > 0) {
          var span = Math.max(.02, o.span / 1000), longest = 0;
          out.forEach(function (p) { longest = Math.max(longest, p.ttl - p.life); });
          out.forEach(function (p) { p._clock = longest / span; });
        }
        if (o.hold > 0) {
          var end = Math.max(.04, o.hold / 1000), landAt = Math.min(end * .8, (o.span || o.hold * .55) / 1000);
          if (_rk >= 1) out.push({mode:'line',x0:x,y0:y,dx:tx-x,dy:ty-y,x:x,y:y,k0:0,vk:1 / landAt,
            life:0,ttl:landAt,r:2,w:1.5,glyph:'mote',color:col});
          out = out.concat(catalogLandingParticles(o.land || [], landAt, end, _rk));
          if (_rk >= 4) out.push({mode:'ring',x:finiteNumber(o.bx,x),y:finiteNumber(o.by,y),r:10,vr:140,
            life:-landAt,ttl:end-landAt,w:2,color:hot});
          if (_rk === 5) for (var crown = 0; crown < 5; crown++) out.push({mode:'orbit',cx:x,cy:y-14,rr:24,
            a0:3.1416 + crown * .7854,va:0,vy:-12,a:0,life:0,ttl:end,r:4,glyph:'crown',color:hot});
        }
        return out;
      },
      step: function (p, dt) {
        dt *= p._clock || 1;
        p.life += dt;
        if (p.life < 0) return true;
        if (p.mode === 'ring' || p.mode === 'plate') { p.r += p.vr * dt; p.vr *= (1 - 2.9 * dt); return p.life < p.ttl; }
        if (p.mode === 'orbit') {
          p.a0 += p.va * dt;
          if (p.shrink) p.rr = Math.max(3, p.rr - p.shrink * dt);
          p.cy += (p.vy || 0) * dt;
          p.x = p.cx + Math.cos(p.a0) * p.rr;
          p.y = p.cy + Math.sin(p.a0) * p.rr * 0.6;
          p.a += (p.spin || 1.5) * dt;
          return p.life < p.ttl;
        }
        if (p.mode === 'line') {
          p.k0 += p.vk * dt;
          var k = Math.min(1, p.k0);
          p.x = p.x0 + p.dx * k; p.y = p.y0 + p.dy * k;
          return p.life < p.ttl && p.k0 < 1.15;
        }
        p.x += p.vx * dt; p.y += p.vy * dt;
        p.vx *= (1 - 1.8 * dt);
        p.vy = p.vy * (1 - 0.9 * dt) + (p.g || 0) * dt;
        p.a += (p.spin || 0) * dt;
        return p.life < p.ttl;
      },
      draw: function (c, p) {
        if (p.life < 0) return;
        var k = Math.max(0, 1 - p.life / p.ttl);
        if (p.mode === 'ring' || p.mode === 'plate') {
          c.globalAlpha = k * 0.8; c.strokeStyle = p.color; c.lineWidth = p.w * k + 0.3;
          c.beginPath();
          if (p.mode === 'plate') {
            for (var corner = 0; corner < 6; corner++) {
              var angle = corner * 6.2832 / 6;
              var px = p.x + Math.cos(angle) * p.r, py = p.y + Math.sin(angle) * p.r;
              if (!corner) c.moveTo(px,py); else c.lineTo(px,py);
            }
            c.closePath();
          } else c.arc(p.x, p.y, Math.max(0,p.r), 0, 6.2832);
          c.stroke();
          return;
        }
        c.globalAlpha = k * 0.9;
        c.fillStyle = p.color; c.strokeStyle = p.color;
        c.lineWidth = Math.max(0.6, (p.w || p.r * 0.45));
        c.lineCap = 'round';
        catalogGlyphDraw(c, p);
      }
    };
  }
  /* R669: one catalog emit includes its landings. Standalone shield and
     bind use the same spawn/step/draw program, under 40 particles and 1.2s. */
  function catalogLandingParticles(lands, delay, end, rarity) {
    var out = [];
    (lands || []).slice(0,3).forEach(function (land) {
      var n = Math.min(10, 5 + rarity), ttl = Math.max(.02, end - delay), color = land.color || '#e8c674';
      for (var i = 0; i < n; i++) {
        var angle = 6.2832 * i / n;
        if (land.verb === 'shield' || land.verb === 'bind') {
          if (i > 2) continue;
          out.push({mode:land.verb === 'shield' ? 'plate' : 'ring',x:land.x,y:land.y,
            r:12 + i * 7,vr:-16,life:-delay,ttl:ttl,w:1.7,color:color});
        } else {
          out.push({mode:'free',x:land.x,y:land.y,vx:Math.cos(angle)*40,vy:Math.sin(angle)*30-25,
            g:0,a:0,spin:0,life:-delay,ttl:ttl,r:land.verb === 'heal' ? 4 : 2,
            glyph:land.verb === 'heal' ? 'heart' : 'mote',color:color});
        }
      }
    });
    return out;
  }
  function abilityDeliveryFx(verb) {
    var program = catalogFx({template:'burst',glyph:'mote',color:'#b3f5ee',particles:4,duration_ms:400}, 2);
    return {spawn:function(o) {
      return catalogLandingParticles([{x:o.x,y:o.y,verb:verb,color:o.color}], 0, Math.min(1200,o.span || 400)/1000,2);
    },step:program.step,draw:program.draw};
  }
  EFFECTS.shield = abilityDeliveryFx('shield');
  EFFECTS.bind = abilityDeliveryFx('bind');
  /* one recipe, one cached program — the cache key is the recipe's own id */
  var catalogFxCache = {};
  function catalogEffectFor(o) {
    var recipe = o && o.recipe;
    if (!recipe || !own(CATALOG_TEMPLATES, String(recipe.template || ''))) return null;
    /* R638: the rarity is part of what the built effect IS now, so it is part
       of the cache key. Every compiled recipe already has a unique colour, so
       this cannot collide today; keying on it anyway means a future recipe
       shared across rarities still gets its own scaled effect instead of
       whichever rarity happened to fire first. */
    var rar = Math.max(0, Math.min(5, (o && o.rarity) | 0));
    var key = String(recipe.id || (recipe.template + '|' + recipe.color + '|' + recipe.glyph)) + '|r' + rar;
    if (!own(catalogFxCache, key)) {
      var fx = catalogFx(recipe, rar);
      if (!fx) return null;
      catalogFxCache[key] = fx;
    }
    return catalogFxCache[key];
  }
  /* Class cards share the catalog particle program, but own a smaller hard
     ceiling.  Keep particles from both ends of an over-budget sequence so a
     dense chained cast never loses either its source burst or its landing. */
  var classCardFxCache = {};
  function classCardParticleCap(list, cap) {
    list = list || []; cap = Math.max(1, Math.min(CLASS_CARD_MOTE_CAP, cap | 0));
    if (list.length <= cap) return list;
    var head = Math.max(1, Math.ceil(cap * 0.58));
    return list.slice(0, head).concat(list.slice(list.length - (cap - head)));
  }
  function classCardEffectFor(o) {
    o = o || {};
    var descriptor = classCardPresentation(o.card || {}, o);
    var index = Math.max(0, o.effectIndex | 0);
    var effect = descriptor && descriptor.effects[index];
    if (!effect || !catalogRenderable(effect.recipe)) return null;
    var key = effect.id + '|r' + descriptor.rarity + '|s' + (descriptor.signature ? 1 : 0);
    if (!own(classCardFxCache, key)) {
      var base = catalogFx(effect.recipe, descriptor.rarity);
      if (!base) return null;
      (function (program, limit) {
        classCardFxCache[key] = {
          spawn: function (opts) { return classCardParticleCap(program.spawn(opts), limit); },
          step: program.step,
          draw: program.draw
        };
      })(base, effect.maxParticles);
    }
    return classCardFxCache[key];
  }
  /* the render contract, answerable without drawing: is this recipe one the
     canvas can honour? The verb harness walks every catalog row through it. */
  function catalogRenderable(recipe) {
    if (!recipe || typeof recipe !== 'object') return false;
    if (!own(CATALOG_TEMPLATES, String(recipe.template || ''))) return false;
    if (typeof recipe.color !== 'string' || !/^#[0-9a-f]{6}$/i.test(recipe.color)) return false;
    if (typeof recipe.reduced_motion !== 'string' || !recipe.reduced_motion.length) return false;
    var n = finiteNumber(recipe.max_particles, finiteNumber(recipe.particles, 0));
    if (!(n > 0 && n <= CATALOG_MOTE_CAP)) return false;
    return true;
  }
  var PRESENTATION_IDS = Object.keys(PRESENTATION_RECIPES).sort();
  for (var presentationIndex = 0; presentationIndex < PRESENTATION_IDS.length; presentationIndex++) {
    var presentationId = PRESENTATION_IDS[presentationIndex];
    EFFECTS[presentationId] = themedFx(PRESENTATION_RECIPES[presentationId]);
  }
  /* R523: THE SEALED COURT's encounter cue (Phase 3d). Same recipe grammar as
     every other stable encounter id, so the profile's fx field resolves. */
  PRESENTATION_RECIPES.sealed_court = { shape: 'rune', color: '#9a86c9', hot: '#e8c674', n: 22, speed: 96, ringSpeed: 320, ringWidth: 3, motion: 'unseal', staticCue: 'seal_badge' };
  EFFECTS.sealed_court = themedFx(PRESENTATION_RECIPES.sealed_court);
  /* R818 M6 · CHAPTERS III AND IV (Engine 124). Every visual id the campaign content names (the ten bosses' and minis' K124
     profiles and BOSS124 rows: their identity, their wind-ups, blasts, wards, drains and topples) and every visual the kit layer
     builds at run time ('kit124_' plus the trait, element, kin, champion or ability op that fired; r502 derives the same list
     from the engine's KITS124). Each is a recipe from the existing shapes in its boss's look colours (bg2-fx.js BOSS_LOOK),
     never a drawn creature, at most 20 particles, with its own motion and its own still for reduced motion. Dormant until an
     Engine 124 tape names it. */
  (function () {
    var PRESET = {
      sign: { shape: 'rune', n: 22, cap: 22, speed: 104, lift: 24, gravity: 30, spin: 4, ringSpeed: 300, ringWidth: 3, ttl: 0.8 },
      blast: { shape: 'shard', n: 20, cap: 20, speed: 205, lift: 10, gravity: 150, spread: 70, ringSpeed: 360, ringWidth: 3, ttl: 0.52 },
      arrow: { shape: 'arrow', n: 14, cap: 14, speed: 250, lift: 6, gravity: 40, spread: 40, ringSpeed: 340, ttl: 0.46 },
      windup: { shape: 'rune', n: 16, cap: 16, speed: 46, lift: 30, gravity: -18, spin: 3, stagger: 0.05, ringSpeed: 150, ringWidth: 2, ttl: 0.95 },
      ward: { shape: 'rune', n: 18, cap: 18, speed: 70, lift: 22, gravity: 18, ringSpeed: 240, ringWidth: 3, ttl: 0.8 },
      drain: { shape: 'mote', n: 18, cap: 18, speed: 52, lift: 72, gravity: -12, stagger: 0.04, size: 2.4, ringSpeed: 200, ttl: 0.9 },
      topple: { shape: 'shard', n: 18, cap: 18, speed: 120, lift: 4, gravity: 260, spread: 50, ringSpeed: 260, ttl: 0.6 },
      bind: { shape: 'vine', n: 18, cap: 18, speed: 44, lift: 46, gravity: 80, stagger: 0.03, size: 3, spread: 420, ring: false, rail: true, ttl: 0.6 },
      cut: { shape: 'shard', n: 16, cap: 16, speed: 260, lift: 6, gravity: 90, spread: 30, ringSpeed: 380, ringWidth: 2, ttl: 0.4 },
      rise: { shape: 'mote', n: 18, cap: 18, speed: 40, lift: 86, gravity: -6, stagger: 0.05, size: 2.2, ringSpeed: 200, ttl: 1 },
      swap: { shape: 'vine', n: 16, cap: 16, speed: 80, lift: 20, gravity: 40, spin: 5, ringSpeed: 260, ttl: 0.7 },
      cleanse: { shape: 'petal', n: 16, cap: 16, speed: 60, lift: 64, gravity: 10, spin: 3, ringSpeed: 220, ttl: 0.85 },
      burn: { shape: 'mote', n: 20, cap: 20, speed: 90, lift: 52, gravity: -20, stagger: 0.03, size: 2.6, hotChance: 0.5, ringSpeed: 260, ttl: 0.7 },
      heal: { shape: 'petal', n: 16, cap: 16, speed: 56, lift: 70, gravity: 14, ringSpeed: 210, ttl: 0.9 },
      status: { shape: 'rune', n: 12, cap: 12, speed: 64, lift: 16, gravity: 30, ringSpeed: 220, ringWidth: 2, ttl: 0.6 }
    };
    /* id: [preset, colour, hot, shape (when not the preset's)] */
    var ROWS = {
      still_grove: ['sign', '#3e6b48', '#dde8f5', 'petal'], starry_arrow: ['arrow', '#9fd3f0', '#dde8f5'],
      agrim: ['sign', '#8a6cff', '#c9d4ff'], agrim_windup: ['windup', '#8a6cff', '#c9d4ff'], agrim_falls: ['blast', '#8c8a94', '#c9d4ff'],
      gravity_lance: ['arrow', '#8a6cff', '#ffffff'], sky_shard: ['blast', '#c9d4ff', '#8a6cff'], agrim_topple: ['topple', '#8c8a94', '#c9d4ff'],
      borrowed_chorus: ['bind', '#8fd0ff', '#f4f8ff', 'mote'], cutting_song: ['cut', '#8fd0ff', '#ffb46b'],
      dusk_penitent: ['sign', '#e07a3f', '#e9e1d0', 'mote'], dusk_duel_windup: ['windup', '#e07a3f', '#e9e1d0'], dusk_duel: ['blast', '#e07a3f', '#9e2b2b'],
      dusk_unchain: ['swap', '#4b2e5a', '#e07a3f'],
      thread_blade: ['sign', '#ff8fd1', '#d9d3e8', 'shard'], thread_cut: ['cut', '#ff8fd1', '#ffffff'], thread_ward: ['ward', '#ff8fd1', '#d9d3e8'],
      loom_ward: ['ward', '#c04fa6', '#d9d3e8'], dylora_twin_windup: ['windup', '#ff8fd1', '#5a2d82'], twin_strike: ['cut', '#d23b2b', '#ff8fd1'],
      dylora_thirst: ['drain', '#c04fa6', '#ff8fd1'],
      vengeful_blade: ['sign', '#e8452c', '#a9b0bc', 'shard'], brace: ['ward', '#a9b0bc', '#e8452c'], riposte: ['cut', '#a9b0bc', '#e8452c'],
      sharper: ['status', '#e8452c', '#a9b0bc'],
      reforged_fury: ['sign', '#d23b2b', '#cfd8e6', 'gear'], shield_bank: ['ward', '#8a97a8', '#cfd8e6'], fury_strike: ['blast', '#d23b2b', '#ff6a3d'],
      nyx_twin_windup: ['windup', '#d23b2b', '#cfd8e6'],
      last_champion: ['sign', '#d8b04c', '#c9b79c', 'shard'], quake_windup: ['windup', '#7c7f86', '#d8b04c'], earthquake: ['blast', '#6e5034', '#d8b04c'],
      quake_stun: ['bind', '#7c7f86', '#d8b04c', 'shard'], haymaker: ['blast', '#d8b04c', '#ffffff'],
      dark_weaving: ['sign', '#e3e6f0', '#e3a33b', 'vine'],
      grey_king: ['sign', '#8e8a88', '#d3361f', 'mote'], chains_of_debt: ['ward', '#4c4f55', '#d3361f'], grey_rise: ['rise', '#8e8a88', '#d3361f'],
      chain_burn: ['burn', '#d3361f', '#ffb06f'], oran_thirst: ['drain', '#7a1410', '#d3361f'], chain_snap: ['topple', '#4c4f55', '#d3361f'],
      hellfire: ['burn', '#ff5a1f', '#ffd24a'],
      kit124_blast: ['blast', '#ff7a6b', '#ffd0a3'], kit124_strike: ['cut', '#ff7a6b', '#fff0c4'], kit124_fixed: ['cut', '#e8452c', '#ffd0a3'],
      kit124_heal: ['heal', '#8fe39a', '#e9ffe9'], kit124_shield: ['ward', '#9fc8ff', '#e6f2ff'], kit124_summon: ['rise', '#ff9ad5', '#ffe0f0'],
      kit124_party: ['status', '#c49eff', '#efe2ff'], kit124_buff: ['status', '#ffd35a', '#fff3b0', 'mote'], kit124_silence: ['status', '#b9a6ff', '#ffffff', 'mote'],
      kit124_taunt: ['status', '#ff6b5a', '#ffd0a3', 'shard'], kit124_cleanse_b: ['cleanse', '#f4f8ff', '#c9d4ff'], kit124_hook: ['swap', '#c8c2d0', '#ffffff'],
      kit124_net: ['bind', '#c9b79c', '#fff6e0'], kit124_weaken: ['status', '#8c8a94', '#d8d4e0', 'petal'], kit124_strip: ['topple', '#a9b0bc', '#ffffff'],
      kit124_lose_health: ['drain', '#9e2b2b', '#ff8a95'], kit124_mirror: ['cut', '#cfd8e6', '#ffffff'], kit124_dodge: ['status', '#7fe6d6', '#e0fff9', 'arrow'],
      kit124_doom: ['windup', '#9a63f2', '#e3d0ff'], kit124_aura: ['rise', '#e8c674', '#fff1c2'], kit124_rise_once: ['rise', '#b6aea4', '#f3ead5'],
      kit124_thornhide: ['cut', '#7a9b45', '#d8ef9b'], kit124_regrow: ['heal', '#6fce4a', '#c8f09a'], kit124_warded: ['ward', '#5ab8ff', '#c8ecff'],
      kit124_vampiric: ['drain', '#b0102a', '#ff4d5e'], kit124_explosive: ['blast', '#ff7a2e', '#ffc27a'], kit124_cleansing: ['cleanse', '#f4f8ff', '#8fdcf5'],
      kit124_fey_step: ['cut', '#ff7aa8', '#ffd6e8'], kit124_fire_burn: ['burn', '#ff5a1f', '#ffd24a'], kit124_earth_armor: ['ward', '#9e7b52', '#e9d8b5'],
      kit124_stout: ['ward', '#7c7f86', '#d8b04c'], kit124_thirst: ['drain', '#d23b2b', '#ff8a95'], kit124_blood_noble: ['drain', '#7a1410', '#ff4d5e'],
      kit124_hellish_rebuke: ['burn', '#e8452c', '#ffb06f']
    };
    var ids = Object.keys(ROWS);
    for (var q = 0; q < ids.length; q++) {
      var id = ids[q], row = ROWS[id], base = PRESET[row[0]], r = {};
      for (var k in base) if (own(base, k)) r[k] = base[k];
      r.color = row[1]; r.hot = row[2]; if (row[3]) r.shape = row[3];
      r.motion = id + '_' + row[0]; r.staticCue = id + '_still';
      PRESENTATION_RECIPES[id] = r;
      EFFECTS[id] = themedFx(r);
    }
  })();
  PRESENTATION_IDS = Object.keys(PRESENTATION_RECIPES).sort();

  /* A fresh descriptor is returned on every call, so a caller cannot mutate
     the canonical palette/cap for later battles. The static cue is the
     reduced-motion contract: UI may replace movement with that persistent
     icon/state while retaining the same semantic id and colors. */
  function presentationDescriptor(name, hint) {
    if (!own(PRESENTATION_RECIPES, name)) return abilityPresentationDescriptor(name, hint);
    var r = PRESENTATION_RECIPES[name];
    var motes = presentationMoteCap(r);
    return {
      id: name,
      shape: r.shape || 'mote',
      color: r.color || '#e8c674',
      hot: r.hot || '#fff6d8',
      motion: r.motion,
      staticCue: r.staticCue,
      maxParticles: motes + (r.ring === false ? 0 : 1)
    };
  }

  /* R753: approved class-move geometry, sharing the existing canvas/pool.
     A move owns a logical clock: speed changes and pause affect its dice,
     impacts and particles together. No time is borrowed from a later tape. */
  var CLASS_MOVE_PART_CAP = 40, CLASS_MOVE_POOL_CAP = 180;
  function registerClassMoveEffect(name, definition) {
    var spawn = definition.spawn, step = definition.step, draw = definition.draw;
    definition.spawn = function (options) {
      var o = Object.assign({}, options);
      if (o.n != null) o.n = Math.max(1, Math.min(CLASS_MOVE_PART_CAP, finiteNumber(o.n, 1) | 0));
      var born = spawn(o) || [];
      return born.slice(0, CLASS_MOVE_PART_CAP).map(function (p) {
        p._classMove = true; p._classClock = o.clock || null; p._classForeground = !!o.foreground;
        p._classAt = o.clock ? o.clock.time : 0;
        return p;
      });
    };
    definition.step = function (p, dt) {
      var clock = p._classClock;
      if (clock) {
        if (clock.cancelled || !clock.alive()) return false;
        dt = Math.max(0, (clock.time - p._classAt) / 1000);
        p._classAt = clock.time;
      }
      var remaining = Math.min(1.6, dt);
      do {
        var slice = Math.min(.04, remaining);
        if (!step(p, slice)) return false;
        remaining -= slice;
      } while (remaining > .00001);
      return true;
    };
    definition.draw = function (c, p) {
      var g = p.ghosts && p.ghosts.length ? p.ghosts : null;
      c.save(); try { if (p.holes && p.holes.length) clipHoles(c, p.holes); if (g) clipHoles(c, g); draw(c, p); } finally { c.restore(); }
      if (g) { c.save(); try { if (p.holes && p.holes.length) clipHoles(c, p.holes); clipTo(c, g); drawGhosted(c, p, draw); } finally { c.restore(); } }
    };
    EFFECTS[name] = definition;
  }
  /* 2026-09-28 (BFX-2) · a thrown piece on the arena board also hands in `ghosts`: the bystander cards its route passes
     (CSS px boxes, like holes). It draws at full strength everywhere else (pass 1 clips the ghosts out, as holes) and
     over those cards at 30% (pass 2 clips to them), the strike trail's own ghost, so only the actor and the struck card
     are ever lit in full. Every draw sets globalAlpha itself (stroke() and glow() below, the volley, the lash, the
     stream), so pass 2 scales each write through an own accessor on the context for the length of that one draw (the
     same two functions every time, so the context keeps one shape). */
  var GHOST_ALPHA = .3, ghostK = 1, alphaDesc = null;
  function ghostGet() { return alphaDesc.get.call(this) / ghostK; }
  function ghostSet(v) { alphaDesc.set.call(this, v * ghostK); }
  function drawGhosted(c, p, draw) {
    if (!alphaDesc) {
      for (var pr = Object.getPrototypeOf(c); pr && !alphaDesc; pr = Object.getPrototypeOf(pr)) {
        var d = Object.getOwnPropertyDescriptor(pr, 'globalAlpha'); if (d && d.get && d.set) alphaDesc = d;
      }
      if (!alphaDesc) return;
    }
    try { Object.defineProperty(c, 'globalAlpha', { configurable:true, get:ghostGet, set:ghostSet }); } catch (_) { return; }
    ghostK = GHOST_ALPHA;
    try { alphaDesc.set.call(c, alphaDesc.get.call(c) * GHOST_ALPHA); draw(c, p); }
    finally { ghostK = 1; try { delete c.globalAlpha; } catch (_) {} }
  }
  function clipTo(c, rects) {
    c.beginPath();
    for (var i = 0; i < rects.length && i < 24; i++) { var r = rects[i]; if (r && r.w > 0 && r.h > 0) c.rect(r.x, r.y, r.w, r.h); }
    c.clip();
  }
  /* 2026-09-27 · a class move on the arena board hands the dice well's words and dice in as `holes` (CSS px boxes,
     x/y/w/h): nothing it draws lands on them, so the roll and its result always read (the strike trail's mask, on
     this canvas). Each hole is its own clip, and clips intersect, so two holes that overlap both stay holes. */
  function clipHoles(c, holes) {
    /* 2026-10-01 (smoothness): the canvas's kept CSS size (resize), not innerWidth/innerHeight, which forced a style pass
       on every draw with holes (176 in a 7 s round on a throttled phone); the box only has to cover the canvas */
    var W = cssW || innerWidth, H = cssH || innerHeight;
    for (var i = 0; i < holes.length && i < 24; i++) {
      var h = holes[i];
      if (!h || !(h.w > 0) || !(h.h > 0)) continue;
      c.beginPath(); c.rect(-W, -H, W * 3, H * 3); c.rect(h.x, h.y, h.w, h.h); c.clip('evenodd');
    }
  }
/* Proposal effects registered with the game's existing canvas renderer. */
(() => {
  const clamp=(n,a=0,b=1)=>Math.max(a,Math.min(b,n)),mix=(a,b,t)=>a+(b-a)*t,TAU=Math.PI*2;
  const reg = registerClassMoveEffect;
  const age=(p,dt)=>(p.life+=dt)<p.ttl;
  /* 2026-09-27 · the way a thrown shape travels from (x,y) to (tx,ty): a straight line, or, when the arena board hands
     it `via` (the strike trail's control point), the trail's own curve round the dice well. heading() is its direction there. */
  const route=(p,v)=>{if(p.via){const w=1-v;return{x:w*w*p.x+2*w*v*p.via.x+v*v*p.tx,y:w*w*p.y+2*w*v*p.via.y+v*v*p.ty};}return{x:mix(p.x,p.tx,v),y:mix(p.y,p.ty,v)};};
  const heading=(p,v)=>{if(p.via){const w=1-v;return Math.atan2(2*w*(p.via.y-p.y)+2*v*(p.ty-p.via.y),2*w*(p.via.x-p.x)+2*v*(p.tx-p.via.x));}return Math.atan2(p.ty-p.y,p.tx-p.x);};
  reg('classmove_bench_stream', {
    spawn:o=>[{...o,life:0,ttl:o.ttl||(o.heal?.5:.28)}],step:age,
    draw:(c,p)=>{
      const t=clamp(p.life/p.ttl),head=clamp(t/(p.heal?.65:1)),dx=p.tx-p.x,dy=p.ty-p.y;
      const bend=Math.min(28,Math.hypot(dx,dy)*.1),col=p.color||'#a4ceb0',tail=Math.max(0,head-.3);
      const point=u=>({x:p.x+dx*u,y:p.y+dy*u-Math.sin(u*Math.PI)*bend});
      const fade=p.heal?1-clamp((t-.7)/.3):1-clamp((t-.9)/.1),path=[];for(let n=0;n<=18;n++)path.push(point(mix(tail,head,n/18)));
      c.globalCompositeOperation='screen';stroke(c,path,col,7,fade*.12);stroke(c,path,col,1.6,fade*.9);
      const q=point(head);glow(c,q.x,q.y,p.heal?13:8,col,fade*.5);
      for(let n=0;n<(p.heal?8:4);n++){const u=clamp(head-n*.025),s=point(u),phase=n*2.4+t*8;
        c.globalAlpha=fade*(1-n/12);c.fillStyle=col;c.beginPath();c.arc(s.x+Math.cos(phase)*4,s.y+Math.sin(phase)*4,1.1,0,TAU);c.fill();}
      if(p.heal&&t>.6){const u=clamp((t-.6)/.4),a=Math.sin(u*Math.PI);
        c.globalAlpha=a*.75;c.strokeStyle=col;c.lineWidth=1.6;c.beginPath();c.ellipse(p.tx,p.ty,12+u*25,7+u*12,0,0,TAU);c.stroke();
        for(let n=0;n<4;n++){const angle=n*TAU/4+.35,rr=14+u*24,xx=p.tx+Math.cos(angle)*rr,yy=p.ty+Math.sin(angle)*rr-u*9;
          c.beginPath();c.moveTo(xx-2,yy);c.lineTo(xx+2,yy);c.moveTo(xx,yy-2);c.lineTo(xx,yy+2);c.stroke();}}
    }
  });
  /* R763 (Jan): "instead of hopping make it feel more satisfying like a
     chain". One link run per bench body: interlocking links pay out from the
     body that just acted, snap to full size one after another, and the last
     one clasps the next body with a small click of light before the whole
     run lets go. Endpoints stop short of both faces so the links sit in the
     gap between tiles. ttl is the chain's own step, so every speed keeps its
     shape and only its tempo changes. */
  reg('bench_chain_link', {
    spawn:o=>[{...o,life:0,ttl:Math.max(.12,o.ttl||.3)}],step:age,
    draw:(c,p)=>{
      const t=clamp(p.life/p.ttl),col=p.color||'#e8c674',ease=v=>1-Math.pow(1-v,3);
      const dx=p.tx-p.x,dy=p.ty-p.y,dist=Math.hypot(dx,dy)||1,ux=dx/dist,uy=dy/dist;
      const cut=Math.min(dist*.3,(p.r||14)*.62),x0=p.x+ux*cut,y0=p.y+uy*cut,x1=p.tx-ux*cut,y1=p.ty-uy*cut;
      const lx=x1-x0,ly=y1-y0,len=Math.hypot(lx,ly)||1,bend=Math.min(14,len*.2);
      let nx=-ly/len,ny=lx/len;if(ny>0){nx=-nx;ny=-ny;}
      const point=u=>{const b=Math.sin(u*Math.PI)*bend;return {x:x0+lx*u+nx*b,y:y0+ly*u+ny*b};};
      const n=Math.max(3,Math.min(28,Math.round(len/8))),gap=len/n;
      const head=ease(clamp(t/.5)),fade=1-clamp((t-.68)/.32);
      c.globalCompositeOperation='screen';
      const under=[];for(let k=0;k<=16;k++)under.push(point(head*k/16));
      stroke(c,under,col,5,.1*fade);
      for(let i=0;i<n;i++){
        const u=(i+.5)/n;if(u>head)break;
        const pop=ease(clamp((head-u)*n*.9)),q=point(u),a=point(Math.max(0,u-.02)),b=point(Math.min(1,u+.02));
        const edge=i%2===1,s=.55+.45*pop,rx=Math.max(1.4,Math.min(5.5,gap*.64)*s),ry=Math.max(.6,(edge?.85:2.4)*s);
        c.save();c.translate(q.x,q.y);c.rotate(Math.atan2(b.y-a.y,b.x-a.x));
        c.globalAlpha=fade*(.5+.5*pop);c.strokeStyle=col;c.lineWidth=edge?1.7:1.25;
        c.beginPath();c.ellipse(0,0,rx,ry,0,0,TAU);c.stroke();c.restore();
      }
      if(t>.4){const k=clamp((t-.4)/.42),a=Math.sin(k*Math.PI);
        glow(c,x1,y1,6+k*6,col,a*.5);
        c.globalAlpha=a*.85;c.strokeStyle='#fff6df';c.lineWidth=1.1;c.beginPath();c.arc(x1,y1,2.5+k*6.5,0,TAU);c.stroke();}
    }
  });
  function glow(c,x,y,r,col,alpha=1){c.globalAlpha=alpha;const g=c.createRadialGradient(x,y,0,x,y,r);g.addColorStop(0,col);g.addColorStop(.22,col+'88');g.addColorStop(1,col+'00');c.fillStyle=g;c.beginPath();c.arc(x,y,r,0,TAU);c.fill();}
  function stroke(c,points,color,width,alpha=1){c.globalAlpha=alpha;c.strokeStyle=color;c.lineWidth=width;c.lineCap='round';c.lineJoin='round';c.beginPath();points.forEach((p,i)=>i?c.lineTo(p.x,p.y):c.moveTo(p.x,p.y));c.stroke();}
  reg('classmove_cut',{
    spawn:o=>[{...o,life:0,ttl:o.ttl||(o.heavy?.42:.3),angle:o.angle??-.55,r:o.r||64}],step:age,
    draw:(c,p)=>{
      const t=clamp(p.life/p.ttl),head=1-Math.pow(1-clamp(t/.68),3),tail=Math.max(0,head-.62),fade=Math.pow(1-t,1.3),r=p.r,col=p.color||'#e6dbca';
      const point=u=>({x:(u*2-1)*r,y:(1.35*(u-.5)*(u-.5)-.34)*r});
      c.translate(p.x,p.y);c.rotate(p.angle);c.globalCompositeOperation='screen';
      const points=[];for(let n=0;n<=28;n++)points.push(point(mix(tail,head,n/28)));
      stroke(c,points,col,p.heavy?12:7,fade*.07);
      c.fillStyle=col;c.globalAlpha=fade*.4;c.beginPath();
      for(let n=0;n<=28;n++){const u=n/28,q=points[n],w=Math.sin(u*Math.PI)*(p.heavy?4.4:p.sleek?1.3:2.4);if(n)c.lineTo(q.x,q.y-w);else c.moveTo(q.x,q.y);}
      for(let n=28;n>=0;n--){const u=n/28,q=points[n],w=Math.sin(u*Math.PI)*(p.heavy?4.4:p.sleek?1.3:2.4);c.lineTo(q.x,q.y+w);}c.closePath();c.fill();
      stroke(c,points,'#fff3dc',p.heavy?1.25:.8,fade*.95);
    }
  });
  /* R764 (Jan): "the basic abilities of the melee classes that just does a
     scratch animation is too lame and not noticible, make sure something flies
     out of them and then does the scratch". One thrown strike from the hero to
     the target, eased so it leaves fast and lands with weight, with a short
     trail; the cut, sparks and impact ring are separate emits at arrival.
     shape: crescent (a blade of light), heavy (a wide war crescent), dagger
     (a thrown knife), palm (a chi ring that swells), wedge (a wedge of holy
     light). Sizes are in CSS px at the target's scale. */
  reg('classmove_throw',{
    spawn:o=>[{...o,life:0,ttl:o.ttl||.26}],step:age,
    draw:(c,p)=>{
      const t=clamp(p.life/p.ttl),u=1-Math.pow(1-t,2.1),dx=p.tx-p.x,dy=p.ty-p.y,len=Math.hypot(dx,dy)||1;
      const nx=-dy/len,ny=dx/len,bow=(p.arc==null?.1:p.arc)*len*(dx<0?-1:1);
      const pos=v=>p.via?route(p,v):({x:p.x+dx*v+nx*Math.sin(v*Math.PI)*bow,y:p.y+dy*v+ny*Math.sin(v*Math.PI)*bow});
      const head=pos(u),ahead=pos(Math.min(1,u+.03)),dir=Math.atan2(ahead.y-head.y,ahead.x-head.x)||Math.atan2(dy,dx);
      const col=p.color||'#e6dbca',size=p.size||14,shape=p.shape||'crescent',fade=t>.9?clamp((1-t)/.1):clamp(t/.08);
      c.globalCompositeOperation='screen';
      const trail=[];for(let k=0;k<=14;k++)trail.push(pos(Math.max(0,u-.3)+Math.min(.3,u)*k/14));
      const wide=shape==='heavy'?size*1.1:shape==='dagger'?3:shape==='palm'?size*.8:size*.7;
      stroke(c,trail,col,wide,fade*.12);stroke(c,trail,col,shape==='dagger'?1:1.5,fade*.5);
      if(shape==='heavy'&&p.dust!==false){for(let k=1;k<5;k++){const q=pos(Math.max(0,u-k*.07));glow(c,q.x,q.y,size*.5,'#8e6a4c',fade*.12);}}
      c.save();c.translate(head.x,head.y);
      if(shape==='crescent'||shape==='heavy'){
        const s=shape==='heavy'?size*1.35:size;c.rotate(dir);glow(c,s*.2,0,s*1.5,col,fade*.42);
        c.globalAlpha=fade;c.fillStyle=col;c.beginPath();c.moveTo(-s*.15,-s);c.quadraticCurveTo(s*1.05,0,-s*.15,s);c.quadraticCurveTo(s*.4,0,-s*.15,-s);c.fill();
        c.globalAlpha=fade*.95;c.strokeStyle='#fff7e8';c.lineWidth=shape==='heavy'?1.4:1;c.beginPath();c.moveTo(-s*.1,-s*.86);c.quadraticCurveTo(s*.9,0,-s*.1,s*.86);c.stroke();
      }else if(shape==='dagger'){
        c.rotate(dir);glow(c,size*.35,0,size*.9,col,fade*.35);c.globalAlpha=fade;
        c.fillStyle='#f4ecff';c.beginPath();c.moveTo(size,0);c.lineTo(-size*.1,-size*.2);c.lineTo(-size*.1,size*.2);c.closePath();c.fill();
        c.fillStyle=col;c.fillRect(-size*.55,-size*.12,size*.42,size*.24);c.fillRect(-size*.16,-size*.34,size*.1,size*.68);
      }else if(shape==='palm'){
        const r=size*(.55+.55*u);glow(c,0,0,r*1.5,col,fade*.38);c.globalAlpha=fade*.95;c.strokeStyle=col;c.lineWidth=2.2;
        c.beginPath();c.ellipse(0,0,r,r*.78,dir,0,TAU);c.stroke();c.globalAlpha=fade*.5;c.lineWidth=1;c.beginPath();c.ellipse(0,0,r*.6,r*.47,dir,0,TAU);c.stroke();
      }else{
        c.rotate(dir);glow(c,0,0,size*1.6,col,fade*.5);c.globalAlpha=fade;c.fillStyle='#fff3cf';
        c.beginPath();c.moveTo(size,0);c.lineTo(-size*.45,-size*.6);c.lineTo(-size*.1,0);c.lineTo(-size*.45,size*.6);c.closePath();c.fill();
      }
      c.restore();
    }
  });
  reg('classmove_petals',{
    spawn:o=>Array.from({length:o.n||10},(_,j)=>({...o,j,life:-j*.012,ttl:.5+Math.random()*.25,a:Math.random()*TAU,r:12+Math.random()*30,size:2+Math.random()*2})),step:age,
    draw:(c,p)=>{if(p.life<0)return;const t=p.life/p.ttl,a=p.a+t*1.4,x=p.x+Math.cos(a)*p.r*t,y=p.y-42*t+Math.sin(a)*p.r*.35;
      c.save();c.translate(x,y);c.rotate(a);c.globalAlpha=Math.sin(t*Math.PI)*.8;c.fillStyle=p.color||'#b8d49a';c.beginPath();c.moveTo(-p.size,0);c.quadraticCurveTo(0,-p.size*1.15,p.size*1.8,0);c.quadraticCurveTo(0,p.size*.7,-p.size,0);c.fill();c.restore();}
  });
  reg('classmove_guard',{
    spawn:o=>[{...o,life:0,ttl:.65}],step:age,
    draw:(c,p)=>{const t=p.life/p.ttl,r=(p.r||45)*(.88+.12*t),fade=Math.sin(t*Math.PI);c.translate(p.x,p.y);c.globalCompositeOperation='screen';
      c.beginPath();c.moveTo(0,-r);c.bezierCurveTo(r*.36,-r*.75,r*.75,-r*.76,r*.7,-r*.5);c.bezierCurveTo(r*.73,r*.18,r*.39,r*.63,0,r);c.bezierCurveTo(-r*.39,r*.63,-r*.73,r*.18,-r*.7,-r*.5);c.bezierCurveTo(-r*.75,-r*.76,-r*.36,-r*.75,0,-r);
      c.strokeStyle=p.color||'#ead29a';c.globalAlpha=fade*.12;c.lineWidth=9;c.stroke();c.globalAlpha=fade*.85;c.lineWidth=1.3;c.stroke();glow(c,0,0,r*.65,p.color||'#ead29a',fade*.12);}
  });
  reg('classmove_resonance',{
    spawn:o=>[{...o,life:0,ttl:.65}],step:age,
    draw:(c,p)=>{const t=p.life/p.ttl,fade=Math.sin(t*Math.PI);c.globalCompositeOperation='screen';
      for(let j=0;j<3;j++){const pts=[],r=(p.r||54)*(t*.7+.35+j*.2);for(let k=0;k<=30;k++){const u=k/30,a=-1.2+u*2.4;pts.push({x:p.x+Math.sin(a)*r,y:p.y+Math.cos(a)*r*.4-j*6});}stroke(c,pts,p.color||'#e0b7d3',5,fade*.08);stroke(c,pts,p.color||'#e0b7d3',1,fade*(1-j*.2));}}
  });
  reg('classmove_wisp',{
    spawn:o=>[{...o,life:0,ttl:.43}],step:age,
    draw:(c,p)=>{const t=clamp(p.life/.34),fade=clamp((p.ttl-p.life)/.09),pts=[];for(let k=0;k<23;k++){const u=Math.max(0,t-.2)+Math.min(.2,t)*k/22;const q=route(p,u);pts.push({x:q.x+(p.via?0:Math.sin(u*Math.PI)*24),y:q.y});}const tip=pts[pts.length-1];c.globalCompositeOperation='screen';stroke(c,pts,p.color||'#baa1d7',12,fade*.13);stroke(c,pts,p.color||'#baa1d7',2,fade*.9);glow(c,tip.x,tip.y,15,p.color||'#baa1d7',fade*.45);glow(c,tip.x,tip.y,4,'#eee4ff',fade);}
  });
  reg('classmove_mark_trigger',{
    spawn:o=>[{...o,life:0,ttl:.45}],step:age,
    draw:(c,p)=>{const t=p.life/p.ttl,r=(p.r||48)*(1.35-.8*t);c.globalCompositeOperation='screen';glow(c,p.x,p.y,45,p.color||'#d2dca5',Math.sin(t*Math.PI)*.4);c.strokeStyle=p.color||'#d2dca5';c.lineWidth=1.5;c.globalAlpha=Math.sin(t*Math.PI);for(let j=0;j<4;j++){const a=j*TAU/4+t*.8;c.beginPath();c.arc(p.x,p.y,r,a+.1,a+.9);c.stroke();}}
  });
  reg('classmove_blast_impact',{
    spawn:o=>[{...o,life:0,ttl:.65}],step:age,
    draw:(c,p)=>{const t=p.life/p.ttl,r=(p.r||70),grow=1-Math.pow(1-t,3);c.globalCompositeOperation='screen';glow(c,p.x,p.y,r*.7,p.color||'#baaad4',Math.pow(1-t,2)*.6);glow(c,p.x,p.y,r*.17,'#f0e7fa',Math.max(0,1-t*4)*.95);c.globalAlpha=Math.pow(1-t,1.6);c.strokeStyle=p.color||'#baaad4';c.lineWidth=2*(1-t)+.4;c.beginPath();c.ellipse(p.x,p.y,r*grow,r*grow*.8,0,0,TAU);c.stroke();for(let j=0;j<10;j++){const a=j*TAU/10+t*.7,rr=r*grow;c.globalAlpha=(1-t)*.45;c.beginPath();c.arc(p.x+Math.cos(a)*rr,p.y+Math.sin(a)*rr*.8,1.4*(1-t),0,TAU);c.fillStyle='#d1c0e8';c.fill();}}
  });
  reg('classmove_sparks',{
    spawn:o=>Array.from({length:o.n||24},(_,j)=>{const a=(o.angle??Math.random()*TAU)+(o.angle===undefined?0:(Math.random()-.5)*1.8),v=35+Math.random()*(o.power||160);return{...o,x:o.x,y:o.y,vx:Math.cos(a)*v,vy:Math.sin(a)*v,life:-j*.003,ttl:.3+Math.random()*.55,size:.5+Math.random()*1.4};}),
    step:(p,dt)=>{p.life+=dt;if(p.life<0)return true;p.x+=p.vx*dt;p.y+=p.vy*dt;p.vx*=1-dt*1.2;p.vy+=(p.gravity??75)*dt;return p.life<p.ttl;},
    draw:(c,p)=>{if(p.life<0)return;c.globalCompositeOperation='screen';const a=clamp((p.ttl-p.life)/.3);stroke(c,[{x:p.x-p.vx*.028,y:p.y-p.vy*.028},p],p.color||'#e9cf9a',p.size,a);if(p.size>1.5)glow(c,p.x,p.y,5,p.color||'#e9cf9a',a*.25);}
  });
  reg('classmove_dust',{
    spawn:o=>Array.from({length:o.n||20},(_,j)=>({...o,vx:(Math.random()-.5)*(o.power||95),vy:-10-Math.random()*65,r:3+Math.random()*9,life:-j*.006,ttl:.55+Math.random()*.6})),
    step:(p,dt)=>{p.life+=dt;if(p.life<0)return true;p.x+=p.vx*dt;p.y+=p.vy*dt;p.vy+=55*dt;return p.life<p.ttl;},
    draw:(c,p)=>{if(p.life<0)return;glow(c,p.x,p.y,p.r*(1+p.life),p.color||'#8e7a60',Math.sin(p.life/p.ttl*Math.PI)*.2);}
  });
  reg('classmove_wave',{
    spawn:o=>[{...o,life:0,ttl:o.ttl||.8}],step:age,
    draw:(c,p)=>{const t=p.life/p.ttl,r=(p.r||74)*(1-Math.pow(1-t,3));c.globalCompositeOperation='screen';c.globalAlpha=Math.pow(1-t,1.6);c.strokeStyle=p.color||'#d8cab2';c.lineWidth=2.8*(1-t)+.4;c.beginPath();c.ellipse(p.x,p.y,r,r*(p.flat||.48),0,0,TAU);c.stroke();c.globalAlpha*=.18;c.lineWidth=12*(1-t);c.stroke();}
  });
  reg('classmove_sigil',{
    spawn:o=>[{...o,life:0,ttl:o.ttl||1.5}],step:age,
    draw:(c,p)=>{const t=p.life/p.ttl,r=(p.r||52)*(.7+.3*clamp(t/.22)),fade=Math.min(clamp(t/.15),clamp((1-t)/.25)),col=p.color||'#baa6ef';c.translate(p.x,p.y);c.scale(1,p.flat||.6);c.rotate(p.reverse?-t*.9:t*.65);c.globalCompositeOperation='screen';c.strokeStyle=col;c.globalAlpha=fade*.7;c.lineWidth=1.3;
      for(const k of [1,.76]){c.beginPath();for(let j=0;j<4;j++)c.arc(0,0,r*k,j*Math.PI/2+.08,j*Math.PI/2+1.32);c.stroke();}
      c.lineWidth=.8;for(let j=0;j<24;j++){const a=j*TAU/24;c.beginPath();c.moveTo(Math.cos(a)*r*.82,Math.sin(a)*r*.82);c.lineTo(Math.cos(a)*r*(j%3===0?.98:.91),Math.sin(a)*r*(j%3===0?.98:.91));c.stroke();}
      if(p.bind){c.globalAlpha*=.6;for(let j=0;j<3;j++){c.save();c.rotate(j*TAU/3+t);c.beginPath();c.ellipse(0,0,r*.3,r*1.15,0,0,TAU);c.stroke();c.restore();}}
      glow(c,0,0,r*.55,col,fade*.14);
    }
  });
  reg('classmove_filament',{
    spawn:o=>Array.from({length:o.n||7},(_,j)=>({...o,j,life:-j*.025,ttl:o.ttl||.65})),step:age,
    draw:(c,p)=>{if(p.life<0)return;const t=p.life/p.ttl,head=clamp(t*1.45),tail=Math.max(0,head-.27),points=[];for(let k=0;k<19;k++){const u=mix(tail,head,k/18),s=Math.sin(u*Math.PI),w=Math.sin(u*11+p.j*1.7);const q=route(p,u);points.push({x:q.x+(p.via?0:s*(p.bend||28))+w*7*s,y:q.y+Math.cos(u*12+p.j)*9*s});}c.globalCompositeOperation='screen';const a=clamp((1-t)/.2);stroke(c,points,p.color||'#a49ae1',3,a*.12);stroke(c,points,p.color||'#a49ae1',.85,a*.8);}
  });
  reg('classmove_lightning',{
    spawn:o=>{const make=(a,b,n,spread)=>Array.from({length:n+1},(_,j)=>({x:mix(a.x,b.x,j/n)+(j&&j<n?(Math.random()-.5)*spread:0),y:mix(a.y,b.y,j/n)+(j&&j<n?(Math.random()-.5)*spread:0)}));const path=o.via?Array.from({length:13},(_,j)=>{const q=route(o,j/12);return{x:q.x+(j&&j<12?(Math.random()-.5)*27:0),y:q.y+(j&&j<12?(Math.random()-.5)*27:0)};}):make(o,{x:o.tx,y:o.ty},12,27),branches=[];for(let j=3;j<10;j+=3){const q=path[j];branches.push(make(q,{x:q.x+(Math.random()-.5)*85,y:q.y+(Math.random()-.5)*70},5,15));}return[{...o,path,branches,life:0,ttl:o.ttl||.55}];},step:age,
    draw:(c,p)=>{const t=p.life/p.ttl,flash=Math.pow(1-t,1.6);c.globalCompositeOperation='screen';stroke(c,p.path,p.color||'#c19ce9',7,flash*.09);stroke(c,p.path,p.color||'#c19ce9',2,flash*.75);stroke(c,p.path,'#f7f0e5',.65,flash);p.branches.forEach(b=>stroke(c,b,p.color||'#c19ce9',.7,flash*.7));}
  });
  reg('classmove_instability',{
    spawn:o=>[{...o,life:0,ttl:o.ttl||.7}],step:age,
    draw:(c,p)=>{const t=p.life/p.ttl,turn=t*5,r=(p.r||61)*(t<.32?1-t*1.7:.46+(t-.32)*1.8),fade=Math.sin(t*Math.PI);c.globalCompositeOperation='screen';
      const color=p.color||'#d0a2f2';glow(c,p.x,p.y,r*.85,color,fade*.22);
      for(let j=0;j<3;j++){const pts=[];for(let k=0;k<28;k++){const u=k/27,a=turn+j*TAU/3+u*1.6,rr=r*(.5+u*.55)+Math.sin(a*3+t*12)*4;pts.push({x:p.x+Math.cos(a)*rr,y:p.y+Math.sin(a)*rr*.72});}stroke(c,pts,color,10,fade*.13);stroke(c,pts,color,2.2,fade);stroke(c,pts,'#fff1ff',.6,fade*.65);}
      for(let j=0;j<16;j++){const a=j*2.399+turn*1.3,rr=r*(.45+(j%5)*.14),x=p.x+Math.cos(a)*rr,y=p.y+Math.sin(a)*rr*.72;stroke(c,[{x:x+Math.sin(a)*7,y:y-Math.cos(a)*5},{x,y}],color,1.4,fade*.85);if(j%3===0)glow(c,x,y,4,color,fade*.6);}
      if(t>.32)glow(c,p.x,p.y,r*.5,'#f4e8ff',Math.max(0,1-(t-.32)*3)*.46);
    }
  });
  reg('classmove_surge_cast',{
    spawn:o=>['#d0a2f2','#98e0e2','#f1c08f'].map((color,j)=>({...o,color,j,life:-j*.016,ttl:o.ttl||.32})),step:age,
    draw:(c,p)=>{if(p.life<0)return;const t=p.life/p.ttl,head=clamp(t*1.35),tail=Math.max(0,head-.3),points=[],scale=p.r||1;
      for(let k=0;k<24;k++){const u=mix(tail,head,k/23),arc=Math.sin(u*Math.PI),phase=u*19-t*5+p.j*TAU/3;const q=route(p,u);points.push({x:q.x+arc*((p.via?0:24)+Math.cos(phase)*13)*scale,y:q.y+Math.sin(phase)*arc*13*scale});}
      c.globalCompositeOperation='screen';const fade=clamp((1-t)/.2);stroke(c,points,p.color,8*scale,fade*.2);stroke(c,points,p.color,2*scale,fade);stroke(c,points,'#fff3ff',.6*scale,fade*.85);
      const tip=points[points.length-1];glow(c,tip.x,tip.y,8*scale,p.color,fade*.65);
    }
  });
  reg('classmove_smoke',{
    spawn:o=>Array.from({length:o.n||24},(_,j)=>({...o,x:o.x+(Math.random()-.5)*(o.w||70),y:o.y+(Math.random()-.5)*65,life:-j*.01,ttl:.7+Math.random()*.7,r:9+Math.random()*21,vx:(Math.random()-.5)*45,vy:-15-Math.random()*20})),
    step:(p,dt)=>{p.life+=dt;if(p.life<0)return true;p.x+=p.vx*dt;p.y+=p.vy*dt;return p.life<p.ttl;},
    draw:(c,p)=>{if(p.life<0)return;glow(c,p.x,p.y,p.r*(1+p.life),p.color||'#80718d',Math.sin(p.life/p.ttl*Math.PI)*.17);}
  });
  reg('classmove_volley',{
    spawn:o=>Array.from({length:o.n||14},(_,j)=>{const tx=o.tx+(Math.random()-.5)*(o.w||90),ty=o.ty+(Math.random()-.5)*50;return{...o,tx,ty,x:tx-(o.dx||110),y:ty-(o.dy||160),life:-j*(o.gap||.055),ttl:.52,depth:.5+Math.random()*.5};}),step:age,
    draw:(c,p)=>{if(p.life<0)return;const t=clamp(p.life/.34),q=route(p,t),x=q.x,y=q.y,a=heading(p,t);c.globalAlpha=clamp((p.ttl-p.life)/.18)*p.depth;c.strokeStyle=p.color||'#d4d0b0';c.lineWidth=p.depth*1.4;c.beginPath();c.moveTo(x-Math.cos(a)*25*p.depth,y-Math.sin(a)*25*p.depth);c.lineTo(x,y);c.stroke();if(t===1){c.globalAlpha*=.5;c.beginPath();c.moveTo(x-3,y+2);c.lineTo(x+3,y-2);c.stroke();}}
  });
  reg('classmove_lash',{
    spawn:o=>[{...o,life:0,ttl:.7}],step:age,
    draw:(c,p)=>{const t=p.life/p.ttl,u=clamp(t/.36),fade=clamp((1-t)/.27),endX=mix(p.x,p.tx,u),endY=mix(p.y,p.ty,u),curl=Math.sin(t*Math.PI)*85;c.globalAlpha=fade;c.lineCap='round';c.strokeStyle='#384b32';c.lineWidth=3.6;c.beginPath();c.moveTo(p.x,p.y);if(p.via){/* the trail's curve cut at u (its control point moves in step), a lighter whip curl on it */const q=route(p,u),cx=mix(p.x,p.via.x,u),cy=mix(p.y,p.via.y,u);c.bezierCurveTo(p.x+(cx-p.x)*2/3+curl*.4,p.y+(cy-p.y)*2/3,q.x+(cx-q.x)*2/3-curl*.4,q.y+(cy-q.y)*2/3,q.x,q.y);}else c.bezierCurveTo(p.x+curl,p.y-90,endX-curl,endY+55,endX,endY);c.stroke();c.strokeStyle='#bfcb96';c.lineWidth=.8;c.stroke();c.globalCompositeOperation='screen';c.strokeStyle='#9bb56a';c.lineWidth=1;c.globalAlpha*=.45;c.stroke();}
  });
  reg('classmove_stream',{
    spawn:o=>Array.from({length:o.n||22},(_,j)=>({...o,j,life:-j*.023,ttl:o.ttl||1,size:.8+Math.random()*1.7})),step:age,
    draw:(c,p)=>{if(p.life<0)return;const t=p.life/p.ttl,u=1-Math.pow(1-t,1.7),s=Math.sin(u*Math.PI),q=route(p,u),x=q.x+s*Math.sin(u*7+p.j)*(p.via?12:28),y=q.y-s*(p.via?(p.j%5-2)*5:22+p.j%5*9);c.globalCompositeOperation='screen';glow(c,x,y,p.size*3,p.color||'#d8d9ab',Math.sin(t*Math.PI)*.6);c.globalAlpha=Math.sin(t*Math.PI)*.9;c.fillStyle='#f7ecd0';c.beginPath();c.arc(x,y,p.size*.6,0,TAU);c.fill();}
  });
  reg('classmove_bloom',{
    spawn:o=>Array.from({length:o.n||22},(_,j)=>({...o,j,life:-j*.018,ttl:1+Math.random()*.65,vx:(Math.random()-.5)*65,vy:-25-Math.random()*65,size:.8+Math.random()*2.2})),
    step:(p,dt)=>{p.life+=dt;if(p.life<0)return true;p.x+=p.vx*dt;p.y+=p.vy*dt;p.vy+=25*dt;return p.life<p.ttl;},
    draw:(c,p)=>{if(p.life<0)return;c.globalCompositeOperation='screen';glow(c,p.x,p.y,p.size*3,p.color||'#c3cc99',clamp((p.ttl-p.life)/.35)*.38);}
  });
  reg('classmove_halo',{
    spawn:o=>[{...o,life:0,ttl:1.2}],step:age,
    draw:(c,p)=>{const t=p.life/p.ttl;c.globalCompositeOperation='screen';glow(c,p.x,p.y,(p.big?90:47)*(.5+t),p.color||'#ead5a9',Math.sin(t*Math.PI)*.22);c.globalAlpha=(1-t)*.5;c.strokeStyle=p.color||'#ead5a9';c.lineWidth=1;c.beginPath();c.ellipse(p.x,p.y,(p.big?75:40)*t,(p.big?30:16)*t,0,0,TAU);c.stroke();}
  });
})();

/* Short, continuous area effects on the existing battle canvas. */
(() => {
  const TAU = Math.PI * 2;
  const clamp = (v, a = 0, b = 1) => Math.max(a, Math.min(b, v));
  const envelope = (t, entry = .12, exit = .32) => Math.min(clamp(t / entry), clamp((1 - t) / exit));
  const reg = registerClassMoveEffect;
  const age = (p, dt) => (p.life += dt) < p.ttl;
  // A filled ellipse keeps gradients off rectangular compositing boundaries.
  function glow(c, x, y, rx, ry, color, alpha) {
    if (alpha <= 0) return;
    c.save();
    c.translate(x, y); c.scale(rx, ry);
    const gradient = c.createRadialGradient(0, 0, 0, 0, 0, 1);
    gradient.addColorStop(0, color);
    gradient.addColorStop(.2, color + '8c');
    gradient.addColorStop(1, color + '00');
    c.globalAlpha = alpha; c.fillStyle = gradient;
    c.beginPath(); c.arc(0, 0, 1, 0, TAU); c.fill();
    c.restore();
  }
  function ribbon(c, path, color, alpha, width = 1) {
    c.lineCap = 'round'; c.lineJoin = 'round';
    c.strokeStyle = color;
    for (const [size, opacity] of [[7, .09], [3, .24], [1, .96]]) {
      c.globalAlpha = alpha * opacity; c.lineWidth = width * size;
      c.beginPath(); path(c); c.stroke();
    }
  }
  function dot(c, x, y, radius, color, alpha) {
    c.globalAlpha = alpha; c.fillStyle = color;
    c.beginPath(); c.arc(x, y, radius, 0, TAU); c.fill();
  }

  // x/y: centre of the enemy-side floor; w: ground width; h: height above it.
  // Main damage beat: .30s. Complete: 1.35s.
  reg('classmove_consecration', {
    spawn: o => [{ ...o, life: 0, ttl: 1.35 }], step: age,
    draw: (c, p) => {
      const t = p.life / p.ttl, fade = envelope(t, .12, .38), color = p.color || '#edca86';
      c.translate(p.x, p.y); c.scale((p.w || 290) / 290, (p.h || 145) / 145);
      c.globalCompositeOperation = 'screen';
      const spread = 1 - Math.pow(1 - clamp(t / .33), 3);
      glow(c, 0, -7, 143 * spread, 32, color, fade * .52);
      glow(c, 0, -35, 134 * spread, 74, '#dca369', fade * .24);
      // Three soft expanding ground ripples, with a bright but thin leading edge.
      for (let i = 0; i < 3; i++) {
        const u = clamp((p.life - i * .13) / .9);
        if (!u || u === 1) continue;
        const r = 25 + 119 * (1 - Math.pow(1 - u, 2));
        ribbon(c, q => q.ellipse(0, -5, r, r * .21, 0, Math.PI * .04, Math.PI * 1.97),
          i === 0 ? '#fff0c5' : color, Math.sin(u * Math.PI) * fade * .48, .7);
      }
      // Flame veils grow directly from the floor, then peel into warm embers.
      for (let j = 0; j < 13; j++) {
        const delay = Math.abs(j - 6) * .014;
        const u = clamp((p.life - .08 - delay) / .91);
        if (!u || u === 1) continue;
        const a = Math.sin(u * Math.PI) * fade;
        const x = (j - 6) * 21, depth = (j % 3 - 1) * 7;
        const height = (34 + ((j * 17) % 47)) * Math.sin(u * Math.PI * .8);
        const lean = Math.sin(j * 1.9 + u * 4.1) * 14;
        const fill = c.createLinearGradient(x, depth + 3, x, depth - height);
        fill.addColorStop(0, color + '00'); fill.addColorStop(.17, color + '88');
        fill.addColorStop(.55, '#fff2c975'); fill.addColorStop(1, color + '00');
        c.fillStyle = fill; c.globalAlpha = a;
        c.beginPath(); c.moveTo(x - 9, depth);
        c.bezierCurveTo(x - 10 + lean, depth - height * .32, x + 7 - lean, depth - height * .72, x + lean, depth - height);
        c.bezierCurveTo(x + 1 + lean, depth - height * .55, x + 11, depth - height * .25, x + 9, depth);
        c.closePath(); c.fill();
        ribbon(c, q => {
          q.moveTo(x, depth - 3);
          q.bezierCurveTo(x + lean * .9, depth - height * .3, x - lean * .7, depth - height * .67, x + lean, depth - height * .93);
        }, '#fff0bd', a * .24, .65);
      }
      for (let i = 0; i < 27; i++) {
        const u = clamp((p.life - (i % 7) * .033) / 1.12);
        if (!u || u === 1) continue;
        const x = Math.sin(i * 2.399) * 134 + Math.sin(u * 4 + i) * 5;
        const y = 4 - u * (27 + i % 7 * 10), alpha = Math.sin(u * Math.PI) * fade;
        dot(c, x, y, .5 + i % 3 * .23, '#fff2c8', alpha * .8);
      }
    }
  });

  // x/y: target-group centre; w/h: diameter of the field. Damage beat: .44s.
  reg('classmove_arcane_field', {
    spawn: o => [{ ...o, life: 0, ttl: 1.3 }], step: age,
    draw: (c, p) => {
      const t = p.life / p.ttl, fade = envelope(t, .12, .31), color = p.color || '#aeb4ef';
      c.translate(p.x, p.y); c.scale((p.w || 260) / 260, (p.h || 175) / 175);
      c.globalCompositeOperation = 'screen';
      const release = clamp((p.life - .25) / .52), expansion = 1 - Math.pow(1 - release, 3);
      glow(c, 0, 0, 91 + expansion * 34, 63 + expansion * 20, color, fade * .24);
      glow(c, 0, 0, 78, 60, '#ebe5ff', Math.exp(-Math.pow((p.life - .44) / .16, 2)) * .8);
      // Open, flowing crescents: no closed emblem, polygon, or wireframe.
      for (let j = 0; j < 5; j++) {
        const phase = t * (j % 2 ? -.9 : 1.05) + j * 1.3;
        const radius = 57 + j * 12 + expansion * 11;
        c.save(); c.rotate(Math.sin(j * 1.7) * .34);
        ribbon(c, q => {
          const start = phase - 1.3, end = phase + 1.6;
          q.ellipse(0, Math.sin(j * 2.3) * 10, radius, radius * (.4 + j * .025), 0, start, end);
        }, j % 2 ? '#d7b9eb' : color, fade * (.95 - j * .07), 1.35 + j * .09);
        c.restore();
      }
      // A single smooth wave unfolds through the target at the damage beat.
      for (let j = 0; j < 3; j++) {
        const u = clamp((p.life - .28 - j * .07) / .74);
        if (!u || u === 1) continue;
        const r = 29 + 106 * (1 - Math.pow(1 - u, 2));
        ribbon(c, q => q.ellipse(0, 0, r, r * .57, -.14, -.25, Math.PI * 1.83),
          j === 0 ? '#eee8ff' : color, Math.sin(u * Math.PI) * .7, 1.1);
      }
      for (let j = 0; j < 24; j++) {
        const angle = j * 2.399 + t * (j % 2 ? .75 : -.65), r = 63 + j % 5 * 11 + expansion * 12;
        const a = fade * (.3 + .3 * Math.sin(j + t * 6) ** 2);
        const x = Math.cos(angle) * r, y = Math.sin(angle) * r * .57;
        dot(c, x, y, j % 4 === 0 ? 1.05 : .55, '#ece7ff', a);
      }
    }
  });

  // x/y: target-group centre; w/h: storm diameter. Ten pulses at .13 + i*.085s.
  // n may override the pulse count, but the whole effect is always <= 1.35s.
  reg('classmove_wild_storm', {
    spawn: o => [{ ...o, n: clamp(o.n || 10, 1, 10), life: 0, ttl: 1.35 }], step: age,
    draw: (c, p) => {
      const t = p.life / p.ttl, fade = envelope(t, .11, .28), color = p.color || '#b1a1ea';
      c.translate(p.x, p.y); c.scale((p.w || 265) / 265, (p.h || 175) / 175);
      c.globalCompositeOperation = 'screen';
      glow(c, 0, -2, 117, 76, color, fade * .23);
      glow(c, 0, 0, 90, 70, '#e5d4f3', Math.exp(-Math.pow((p.life - .94) / .13, 2)) * .68);
      // Two broad counter-curving currents carry the brief individual surges.
      for (let j = 0; j < 4; j++) {
        const rotation = t * 1.85 + j * Math.PI * .51;
        c.save(); c.rotate(rotation);
        ribbon(c, q => {
          q.moveTo(-115, -4);
          q.bezierCurveTo(-103, -47, -29, -62, 23, -35);
          q.bezierCurveTo(81, -5, 60, 32, 19, 28);
          q.bezierCurveTo(-9, 25, -18, 9, -6, -3);
        }, j % 2 ? '#d8b6f3' : '#aee4ed', fade * .9, j % 2 ? 1.8 : 2.4);
        c.restore();
      }
      for(let j=0;j<28;j++){
        const a=j*2.399+t*4.8,r=38+j%6*13;
        dot(c,Math.cos(a)*r,Math.sin(a)*r*.7,1+j%3*.25,j%2?'#ead9ff':'#b8eef0',fade*.85);
      }
      for (let j = 0; j < p.n; j++) {
        const local = p.life - .13 - j * .085;
        if (local < 0 || local > .32) continue;
        const u = local / .32, intensity = Math.sin(u * Math.PI) * (1 - u * .45);
        const angle = j * 2.399 + t * .32;
        const x = Math.cos(angle) * (22 + j % 3 * 18), y = Math.sin(angle) * (15 + j % 3 * 11);
        const pulseColor = j >= 6 ? '#f1c5ad' : (j % 2 ? '#c5b2ef' : '#bedce9');
        glow(c, x, y, 26 + u * 13, 20 + u * 9, pulseColor, intensity * .58);
        c.save(); c.translate(x, y); c.rotate(angle);
        ribbon(c, q => {
          q.moveTo(-60 + u * 18, -12);
          q.bezierCurveTo(-44, -28 - u * 4, -23, 21, -8, 6);
          q.bezierCurveTo(5, -10, 15, -15, 31 + u * 10, -5);
        }, pulseColor, intensity, 1.6);
        ribbon(c, q => {
          q.moveTo(-26, -2);
          q.bezierCurveTo(-9, -23, 19, 6, 31, 13);
        }, '#f2ecfa', intensity * .88, .85);
        c.restore();
        for (let k = 0; k < 3; k++) {
          const a = angle + k * 2.1, dist = 7 + u * 28;
          dot(c, x + Math.cos(a) * dist, y + Math.sin(a) * dist * .7, .55, pulseColor, intensity * .75);
        }
      }
    }
  });
})();


/* R792 (Jan, 2026-09-22 overhaul): run-upgrade trigger painters. The upgrade
   layer (hoh-upgrade-fx.js) used to draw every card as SVG strokes under a
   bottom name pill. It now plays a five-beat grammar (tell, travel, impact,
   result, residue) on this canvas, keyed by the card's mechanic family, so a
   burn reads as embers, a freeze as a crust, a doom as a clock and a shield
   break as cracks without any text. These are registered as class-move
   effects so they ride the caller's logical clock: Pause freezes them, a
   hit-stop holds them still, and a cancelled fight ends them. Every one is
   short (under 1.1 s), bounded by CLASS_MOVE_PART_CAP per emit, and has a
   caller-side static fallback under reduced motion (emit returns false). */
(() => {
  const TAU = Math.PI * 2;
  const clamp = (v, a = 0, b = 1) => Math.max(a, Math.min(b, v));
  const mix = (a, b, t) => a + (b - a) * t;
  const reg = registerClassMoveEffect;
  const age = (p, dt) => (p.life += dt) < p.ttl;
  const ease = t => 1 - Math.pow(1 - clamp(t), 3);
  function glow(c, x, y, r, color, alpha) {
    if (!(alpha > 0) || !(r > 0)) return;
    const g = c.createRadialGradient(x, y, 0, x, y, r);
    g.addColorStop(0, color); g.addColorStop(.25, color + '88'); g.addColorStop(1, color + '00');
    c.globalAlpha = alpha; c.fillStyle = g; c.beginPath(); c.arc(x, y, r, 0, TAU); c.fill();
  }
  function line(c, pts, color, width, alpha) {
    if (!(alpha > 0) || pts.length < 2) return;
    c.globalAlpha = alpha; c.strokeStyle = color; c.lineWidth = width; c.lineCap = 'round'; c.lineJoin = 'round';
    c.beginPath(); pts.forEach((q, i) => i ? c.lineTo(q.x, q.y) : c.moveTo(q.x, q.y)); c.stroke();
  }
  function star(c, x, y, r, rot) {
    c.beginPath();
    for (let k = 0; k < 8; k++) { const a = rot + k * Math.PI / 4, rr = k % 2 ? r * .38 : r; c.lineTo(x + Math.cos(a) * rr, y + Math.sin(a) * rr); }
    c.closePath();
  }
  /* A die face or a shield splitting: jagged cracks run out from the point,
     with a white core flash in the first frames. */
  reg('upgrade_crack', {
    spawn: o => {
      const r = o.r || 26, arms = [];
      for (let k = 0; k < (o.arms || 6); k++) {
        const base = k * TAU / (o.arms || 6) + (Math.random() - .5) * .5, pts = [{ x: 0, y: 0 }];
        let px = 0, py = 0;
        for (let s = 1; s <= 4; s++) { const a = base + (Math.random() - .5) * .7, d = r * s / 4; px = Math.cos(a) * d; py = Math.sin(a) * d; pts.push({ x: px, y: py }); }
        arms.push(pts);
      }
      return [{ ...o, arms, life: 0, ttl: o.ttl || .5 }];
    }, step: age,
    draw: (c, p) => {
      const t = clamp(p.life / p.ttl), grow = ease(t / .3), fade = 1 - clamp((t - .55) / .45), col = p.color || '#ffd27a';
      c.translate(p.x, p.y); c.globalCompositeOperation = 'screen';
      glow(c, 0, 0, (p.r || 26) * .9, '#fff7dc', Math.max(0, 1 - t * 5) * .95);
      p.arms.forEach(pts => {
        const n = Math.max(2, Math.round(pts.length * grow)), part = pts.slice(0, n);
        line(c, part, col, 4, fade * .18); line(c, part, '#fff6e0', 1.2, fade * .95);
      });
    }
  });
  /* Angular shards that fly out, spin and fall: a shield breaking, a frozen
     crust bursting, a die splitting. */
  reg('upgrade_shards', {
    spawn: o => Array.from({ length: o.n || 10 }, (_, j) => {
      const a = (o.angle != null ? o.angle + (Math.random() - .5) * 1.6 : Math.random() * TAU), v = 40 + Math.random() * (o.power || 150);
      return { ...o, j, vx: Math.cos(a) * v, vy: Math.sin(a) * v - (o.lift || 30), spin: (Math.random() - .5) * 14, rot: Math.random() * TAU,
        size: (o.size || 5) * (.6 + Math.random() * .8), life: -j * .008, ttl: .5 + Math.random() * .35 };
    }),
    step: (p, dt) => { p.life += dt; if (p.life < 0) return true; p.x += p.vx * dt; p.y += p.vy * dt; p.vx *= 1 - dt * 1.5; p.vy += (p.gravity ?? 260) * dt; p.rot += p.spin * dt; return p.life < p.ttl; },
    draw: (c, p) => {
      if (p.life < 0) return;
      const a = clamp((p.ttl - p.life) / .25), s = p.size, col = p.color || '#cfe8ff';
      c.translate(p.x, p.y); c.rotate(p.rot); c.globalAlpha = a; c.fillStyle = col;
      c.beginPath(); c.moveTo(-s, -s * .4); c.lineTo(s * .9, -s * .2); c.lineTo(s * .2, s * .8); c.closePath(); c.fill();
      c.globalAlpha = a * .9; c.strokeStyle = '#ffffff'; c.lineWidth = .7; c.stroke();
    }
  });
  /* Armor plates peel off a bar and fly to the target, then burst. x,y: the
     bar; w: its width; tx,ty: the target. */
  reg('upgrade_plates', {
    spawn: o => Array.from({ length: o.n || 4 }, (_, j, arr) => ({ ...o, j, ox: o.x + ((j + .5) / (o.n || 4) - .5) * (o.w || 50),
      life: -j * .045, ttl: o.ttl || .62, tilt: (Math.random() - .5) * 1.2 })),
    step: age,
    draw: (c, p) => {
      if (p.life < 0) return;
      const t = clamp(p.life / p.ttl), col = p.color || '#e3a85c';
      const peel = clamp(t / .28), fly = ease((t - .28) / .6);
      const bend = Math.min(60, Math.hypot(p.tx - p.ox, p.ty - p.y) * .25);
      const x = mix(p.ox, p.tx, fly), y = mix(p.y - peel * 12, p.ty, fly) - Math.sin(fly * Math.PI) * bend;
      const w = 11, h = 7, fade = t > .9 ? clamp((1 - t) / .1) : 1;
      c.translate(x, y); c.rotate(p.tilt * peel + fly * 4);
      c.globalCompositeOperation = 'screen'; glow(c, 0, 0, 14, col, fade * .35);
      c.globalAlpha = fade; c.fillStyle = col; c.strokeStyle = '#fff1d6'; c.lineWidth = .9;
      c.beginPath(); c.moveTo(-w / 2 + 2, -h / 2); c.lineTo(w / 2 - 2, -h / 2); c.lineTo(w / 2, 0); c.lineTo(w / 2 - 2, h / 2); c.lineTo(-w / 2 + 2, h / 2); c.lineTo(-w / 2, 0); c.closePath(); c.fill(); c.stroke();
      if (t > .86) { c.rotate(-(p.tilt * peel + fly * 4)); glow(c, 0, 0, 18 * (t - .86) / .14 + 6, '#fff3dc', (1 - t) * 4); }
    }
  });
  /* Burn: embers cling to the target and rise with a flicker. */
  reg('upgrade_embers', {
    spawn: o => Array.from({ length: o.n || 12 }, (_, j) => ({ ...o, j, x: o.x + (Math.random() - .5) * (o.w || 50), y: o.y + (Math.random() - .2) * (o.h || 40) * .5,
      vy: -30 - Math.random() * 55, vx: (Math.random() - .5) * 18, life: -j * .03, ttl: .55 + Math.random() * .45, size: 1 + Math.random() * 1.8 })),
    step: (p, dt) => { p.life += dt; if (p.life < 0) return true; p.x += p.vx * dt + Math.sin(p.life * 13 + p.j) * .35; p.y += p.vy * dt; return p.life < p.ttl; },
    draw: (c, p) => {
      if (p.life < 0) return;
      const t = p.life / p.ttl, a = Math.sin(t * Math.PI) * (.75 + .25 * Math.sin(p.life * 40 + p.j));
      c.globalCompositeOperation = 'screen'; glow(c, p.x, p.y, p.size * 4, p.color || '#ff8a3d', a * .55);
      c.globalAlpha = a; c.fillStyle = '#ffe2a8'; c.beginPath(); c.arc(p.x, p.y, p.size * .7, 0, TAU); c.fill();
    }
  });
  /* Freeze: an ice crust of spikes grows around the portrait's edge. */
  reg('upgrade_frost', {
    spawn: o => {
      const n = o.n || 12, spikes = Array.from({ length: n }, (_, k) => ({ a: k * TAU / n + (Math.random() - .5) * .3, l: .35 + Math.random() * .45 }));
      return [{ ...o, spikes, life: 0, ttl: o.ttl || .95 }];
    }, step: age,
    draw: (c, p) => {
      const t = clamp(p.life / p.ttl), grow = ease(t / .35), fade = 1 - clamp((t - .7) / .3), col = p.color || '#a8e4ff';
      const rx = (p.w || 56) / 2, ry = (p.h || 56) / 2;
      c.translate(p.x, p.y); c.globalCompositeOperation = 'screen';
      glow(c, 0, 0, Math.max(rx, ry) * 1.1, col, fade * .22 * grow);
      c.strokeStyle = col; c.fillStyle = col;
      p.spikes.forEach(s => {
        const bx = Math.cos(s.a) * rx, by = Math.sin(s.a) * ry, len = Math.min(rx, ry) * s.l * grow;
        const ix = bx - Math.cos(s.a) * len, iy = by - Math.sin(s.a) * len, nx = -Math.sin(s.a) * 3.2, ny = Math.cos(s.a) * 3.2;
        c.globalAlpha = fade * .45; c.beginPath(); c.moveTo(bx + nx, by + ny); c.lineTo(ix, iy); c.lineTo(bx - nx, by - ny); c.closePath(); c.fill();
        c.globalAlpha = fade * .95; c.lineWidth = .9; c.beginPath(); c.moveTo(bx, by); c.lineTo(ix, iy); c.stroke();
      });
      c.globalAlpha = fade * .6; c.lineWidth = 1.4; c.beginPath(); c.ellipse(0, 0, rx, ry, 0, 0, TAU); c.stroke();
    }
  });
  /* Stun: a ring of small stars circles over the head. */
  reg('upgrade_daze', {
    spawn: o => [{ ...o, life: 0, ttl: o.ttl || .9 }], step: age,
    draw: (c, p) => {
      const t = clamp(p.life / p.ttl), fade = Math.min(clamp(t / .15), clamp((1 - t) / .3)), r = p.r || 22, n = p.n || 5, col = p.color || '#ffe7a3';
      c.translate(p.x, p.y); c.globalCompositeOperation = 'screen';
      c.globalAlpha = fade * .45; c.strokeStyle = col; c.lineWidth = 1; c.beginPath(); c.ellipse(0, 0, r, r * .32, 0, 0, TAU); c.stroke();
      for (let k = 0; k < n; k++) {
        const a = k * TAU / n + t * 5.2, x = Math.cos(a) * r, y = Math.sin(a) * r * .32, front = Math.sin(a) > 0 ? 1 : .55;
        glow(c, x, y, 9, col, fade * .5 * front); c.globalAlpha = fade * front; c.fillStyle = '#fff8dc'; star(c, x, y, 5.4, t * 6 + k); c.fill();
      }
    }
  });
  /* Doom: a clock sigil over the target. The hand steps once per tick; with
     implode the ring collapses to a point before the blast. */
  reg('upgrade_clock', {
    spawn: o => [{ ...o, life: 0, ttl: o.ttl || (o.implode ? .5 : .85) }], step: age,
    draw: (c, p) => {
      const t = clamp(p.life / p.ttl), col = p.color || '#b98cff', base = p.r || 26;
      const r = p.implode ? base * (1 - ease(t) * .92) : base * (.8 + .2 * ease(t / .25));
      const fade = p.implode ? 1 - clamp((t - .85) / .15) : Math.min(clamp(t / .15), clamp((1 - t) / .3));
      c.translate(p.x, p.y); c.globalCompositeOperation = 'screen';
      glow(c, 0, 0, r * 1.2, col, fade * .3);
      c.strokeStyle = col; c.globalAlpha = fade * .9; c.lineWidth = 1.6; c.beginPath(); c.arc(0, 0, r, 0, TAU); c.stroke();
      c.lineWidth = 1; for (let k = 0; k < 12; k++) { const a = k * TAU / 12; c.beginPath(); c.moveTo(Math.cos(a) * r * .8, Math.sin(a) * r * .8); c.lineTo(Math.cos(a) * r * (k % 3 ? .9 : .98), Math.sin(a) * r * (k % 3 ? .9 : .98)); c.stroke(); }
      const left = Math.max(0, p.ticks == null ? 3 : p.ticks), step = p.implode ? t * TAU * 1.5 : ease(clamp((t - .2) / .3)) * (TAU / 12);
      const hand = -Math.PI / 2 + (12 - Math.min(12, left)) * TAU / 12 + step;
      c.lineWidth = 2; c.globalAlpha = fade; c.strokeStyle = '#f1e6ff'; c.beginPath(); c.moveTo(0, 0); c.lineTo(Math.cos(hand) * r * .72, Math.sin(hand) * r * .72); c.stroke();
      if (p.implode && t > .8) glow(c, 0, 0, base * .9 * (t - .8) / .2, '#ffffff', (1 - t) * 5);
    }
  });
  /* Bleed: drops fall from the wound and splash. */
  reg('upgrade_drops', {
    spawn: o => Array.from({ length: o.n || 6 }, (_, j) => ({ ...o, j, x: o.x + (Math.random() - .5) * (o.w || 40), y: o.y - (o.h || 40) * .25 + Math.random() * 6,
      floor: o.y + (o.h || 40) * .45, vy: 10 + Math.random() * 30, life: -j * .05, ttl: .7, splash: 0 })),
    step: (p, dt) => { p.life += dt; if (p.life < 0) return true; if (p.y < p.floor) { p.vy += 420 * dt; p.y = Math.min(p.floor, p.y + p.vy * dt); } else p.splash += dt; return p.life < p.ttl; },
    draw: (c, p) => {
      if (p.life < 0) return;
      const col = p.color || '#ff5a6e', a = clamp((p.ttl - p.life) / .2);
      if (p.y < p.floor) {
        c.globalAlpha = a; c.fillStyle = col; c.beginPath(); c.moveTo(p.x, p.y - 5); c.quadraticCurveTo(p.x + 3.2, p.y + 1, p.x, p.y + 2.8); c.quadraticCurveTo(p.x - 3.2, p.y + 1, p.x, p.y - 5); c.fill();
      } else {
        const u = clamp(p.splash / .2); c.globalAlpha = a * (1 - u); c.strokeStyle = col; c.lineWidth = 1.2; c.beginPath(); c.ellipse(p.x, p.floor, 2 + u * 7, 1 + u * 2.2, 0, 0, TAU); c.stroke();
      }
    }
  });
  /* Weaken: a dim veil drapes over the target and drifts down. */
  reg('upgrade_veil', {
    spawn: o => [{ ...o, life: 0, ttl: o.ttl || .8 }], step: age,
    draw: (c, p) => {
      const t = clamp(p.life / p.ttl), fade = Math.min(clamp(t / .2), clamp((1 - t) / .35)), w = p.w || 60, h = p.h || 60, col = p.color || '#b9a3d9';
      c.translate(p.x - w / 2, p.y - h / 2);
      for (let k = 0; k < 3; k++) {
        const y0 = (t * .6 + k * .28) % 1 * h;
        c.globalAlpha = fade * (.3 - k * .06); c.fillStyle = col; c.beginPath(); c.moveTo(0, y0);
        for (let s = 0; s <= 10; s++) { const x = s / 10 * w; c.lineTo(x, y0 + Math.sin(s * .9 + t * 7 + k) * 3.5); }
        for (let s = 10; s >= 0; s--) { const x = s / 10 * w; c.lineTo(x, y0 + 9 + Math.sin(s * .9 + t * 7 + k + 1) * 3.5); }
        c.closePath(); c.fill();
      }
    }
  });
  /* A multiplier stamp: a heavy ring slams down onto the point, then a dust
     ring spreads. The "x2" itself is DOM text drawn by the caller. */
  reg('upgrade_stamp', {
    spawn: o => [{ ...o, life: 0, ttl: o.ttl || .5 }], step: age,
    draw: (c, p) => {
      const t = clamp(p.life / p.ttl), r = p.r || 24, col = p.color || '#ff8a4c';
      const slam = clamp(t / .22), rr = mix(r * 2.3, r, ease(slam));
      c.translate(p.x, p.y); c.globalCompositeOperation = 'screen';
      if (slam < 1) { c.globalAlpha = slam; c.strokeStyle = col; c.lineWidth = 5 * (1 - slam) + 2; c.beginPath(); c.arc(0, 0, rr, 0, TAU); c.stroke(); }
      else {
        const u = clamp((t - .22) / .78);
        glow(c, 0, 0, r * 1.4, col, (1 - u) * .45);
        c.globalAlpha = (1 - u) * .8; c.strokeStyle = col; c.lineWidth = 2; c.beginPath(); c.arc(0, 0, r * (1 + u * 1.2), 0, TAU); c.stroke();
        for (let k = 0; k < 10; k++) { const a = k * TAU / 10, d = r * (1.1 + u * 1.3); c.globalAlpha = (1 - u) * .7; c.fillStyle = '#ffe6c8'; c.beginPath(); c.arc(Math.cos(a) * d, Math.sin(a) * d, 1.4, 0, TAU); c.fill(); }
      }
    }
  });
  /* A dodge or an extra action: ghost outlines of the portrait trail away. */
  reg('upgrade_afterimage', {
    spawn: o => Array.from({ length: o.n || 3 }, (_, j) => ({ ...o, j, life: -j * .05, ttl: o.ttl || .5 })), step: age,
    draw: (c, p) => {
      if (p.life < 0) return;
      const t = clamp(p.life / p.ttl), w = p.w || 56, h = p.h || 70, off = ease(t) * (p.j + 1) * 7, col = p.color || '#ffe08a';
      const x = p.x + (p.dx == null ? -1 : p.dx) * off - w / 2, y = p.y + (p.dy || 0) * off - h / 2, rr = Math.min(10, w * .15);
      c.globalCompositeOperation = 'screen'; c.globalAlpha = (1 - t) * (.7 - p.j * .15); c.strokeStyle = col; c.lineWidth = 1.6;
      c.beginPath(); c.moveTo(x + rr, y); c.lineTo(x + w - rr, y); c.quadraticCurveTo(x + w, y, x + w, y + rr); c.lineTo(x + w, y + h - rr); c.quadraticCurveTo(x + w, y + h, x + w - rr, y + h);
      c.lineTo(x + rr, y + h); c.quadraticCurveTo(x, y + h, x, y + h - rr); c.lineTo(x, y + rr); c.quadraticCurveTo(x, y, x + rr, y); c.stroke();
      c.globalAlpha *= .18; c.fillStyle = col; c.fill();
    }
  });
  /* Self and party buffs: motes gather in from a ring and converge on the
     recipient, then flash once. Used instead of a travel for own-side cards. */
  reg('upgrade_gather', {
    spawn: o => Array.from({ length: o.n || 10 }, (_, j) => ({ ...o, j, a: j * TAU / (o.n || 10) + Math.random() * .4, d: (o.r || 34) * (1.6 + Math.random() * .6), life: -j * .012, ttl: o.ttl || .45 })),
    step: age,
    draw: (c, p) => {
      if (p.life < 0) return;
      const t = clamp(p.life / p.ttl), u = ease(t), d = p.d * (1 - u), a = p.a + u * 1.6, col = p.color || '#8fe39a';
      const x = p.x + Math.cos(a) * d, y = p.y + Math.sin(a) * d * .75;
      c.globalCompositeOperation = 'screen'; glow(c, x, y, 5, col, Math.sin(t * Math.PI) * .8);
      c.globalAlpha = Math.sin(t * Math.PI); c.fillStyle = '#ffffff'; c.beginPath(); c.arc(x, y, 1.1, 0, TAU); c.fill();
      if (p.j === 0 && t > .82) glow(c, p.x, p.y, (p.r || 34) * .9, col, (1 - t) * 3.5);
    }
  });
  /* Revive: a pillar of light stands on the seat while sparks climb it. */
  reg('upgrade_pillar', {
    spawn: o => [{ ...o, life: 0, ttl: o.ttl || .85 }], step: age,
    draw: (c, p) => {
      const t = clamp(p.life / p.ttl), fade = Math.min(clamp(t / .18), clamp((1 - t) / .35)), w = p.w || 34, h = p.h || 150, col = p.color || '#fff1b3';
      const g = c.createLinearGradient(p.x - w / 2, 0, p.x + w / 2, 0);
      g.addColorStop(0, col + '00'); g.addColorStop(.5, col); g.addColorStop(1, col + '00');
      c.globalCompositeOperation = 'screen'; c.globalAlpha = fade * .55; c.fillStyle = g; c.fillRect(p.x - w / 2, p.y - h, w, h + 10);
      for (let k = 0; k < 7; k++) { const u = (t * 1.6 + k / 7) % 1, x = p.x + Math.sin(k * 2.3 + t * 6) * w * .3, y = p.y - u * h;
        c.globalAlpha = fade * (1 - u); c.fillStyle = '#ffffff'; c.beginPath(); c.arc(x, y, 1.3, 0, TAU); c.fill(); }
      glow(c, p.x, p.y, w * 1.4, col, fade * .5);
    }
  });
  /* The 1-2 frame impact flash on a target. */
  reg('upgrade_flare', {
    spawn: o => [{ ...o, life: 0, ttl: o.ttl || .09 }], step: age,
    draw: (c, p) => { const t = clamp(p.life / p.ttl); c.globalCompositeOperation = 'screen'; glow(c, p.x, p.y, p.r || 40, p.color || '#ffffff', (1 - t) * .95); glow(c, p.x, p.y, (p.r || 40) * .35, '#ffffff', 1 - t); }
  });
})();

  registerClassMoveEffect('classmove_grove', themedFx(PRESENTATION_RECIPES.grove_treants));
  registerClassMoveEffect('classmove_growth', themedFx(PRESENTATION_RECIPES.nature_growth));

  /* ---------------- the loop ---------------- */
  function loop(t) {
    raf = 0;
    /* An inspector can open in the middle of a burst. Clear immediately so
       combat sparks never paint through a sheet the player is trying to read. */
    if (blockingSheetOpen()) {
      parts.length = 0;
      if (canvas) canvas.style.zIndex = '100060';
      clearAll(); rest();
      return;
    }
    /* R450b: a THROTTLED tab freezes rAF while the last frame stays
       painted — coming back, the dt clamp below would make every stale
       burst crawl across seconds. A gap over 1.5s means the tab was not
       really animating: cull the stale pool and clear, same policy as
       the hidden-tab listener. */
    var gap = (t - lastT) / 1000;
    if (lastT && gap > 1.5) {
      parts.length = 0;
      canvas.style.zIndex = '100060';
      lastT = t;
      clearAll(); rest();
      return;
    }
    var dt = Math.max(0, Math.min(0.05, gap || 0.016));
    lastT = t;
    clearAll();
    var keep = [], foreground = false;
    /* 2026-09-28: a particle handed a `clip` box (CSS px: the arena board's own box for its rising heal motes) is drawn
       only inside it, so it rises from where it began and ends at that box's edge. One burst's particles sit together
       in the pool, so a run of them sharing a box is clipped once, not once each. */
    var clipOn = null;
    try {
      for (var i = 0; i < parts.length; i++) {
        var p = parts[i];
        if (p._endsAt != null && t >= p._endsAt) continue;
        if (p._fx.step(p, dt)) {
          var k = p.clip && p.clip.w > 0 && p.clip.h > 0 ? p.clip : null;
          if (k !== clipOn) {
            if (clipOn) ctx.restore();
            clipOn = k;
            if (k) { ctx.save(); ctx.beginPath(); ctx.rect(k.x, k.y, k.w, k.h); ctx.clip(); }
          }
          p._fx.draw(ctx, p);
          keep.push(p); if (p._classForeground) foreground = true;
        }
      }
    } finally { if (clipOn) ctx.restore(); }
    ctx.globalAlpha = 1;
    parts = keep;
    var layer = foreground ? '100080' : '100060';
    if (canvas.style.zIndex !== layer) canvas.style.zIndex = layer;
    if (parts.length) raf = requestAnimationFrame(loop);
    else { clearAll(); rest(); }
  }
  function start() {
    wake();
    if (!raf) { lastT = performance.now(); raf = requestAnimationFrame(loop); }
  }
  function stop() {
    if (raf) { cancelAnimationFrame(raf); raf = 0; }
    if (canvas) canvas.style.zIndex = '100060';
    try { clearAll(); rest(); } catch (e) {}
  }

  function emit(name, o) {
    if (reduced || pageReduced()) return false;
    /* Report handled while a blocking sheet is open. Returning false would
       ask callers to create their DOM fallback on top of the same sheet. */
    if (blockingSheetOpen()) { parts.length = 0; stop(); return true; }
    /* R523: 'catalog.fx' renders a concrete catalog fx_recipe handed in with
       the emit — the FX LAW's one battle door for the 469-row catalog. */
    var fx = name === 'catalog.fx' ? catalogEffectFor(o)
      : name === 'classcard.fx' ? classCardEffectFor(o)
      : own(EFFECTS, name) ? EFFECTS[name] : abilityEffect(name, o && o.ability);
    if (!fx || !ensureCanvas()) return false;
    var born;
    try { born = fx.spawn(o || {}) || []; } catch (e) { return false; }
    /* Process at most the global pool. Third-party definitions are supported,
       but may not make the registry walk an unbounded returned array. */
    var firstBorn = Math.max(0, born.length - PART_CAP);
    var endsAt = o && o.hold > 0 ? performance.now() + o.hold : null;
    var clipBox = o && o.clip && o.clip.w > 0 && o.clip.h > 0 ? o.clip : null;
    for (var i = firstBorn; i < born.length; i++) {
      born[i]._fx = fx;
      if (endsAt != null) born[i]._endsAt = endsAt;
      if (clipBox) born[i].clip = clipBox;
      if (parts.length >= PART_CAP) parts.shift();
      parts.push(born[i]);
    }
    if (String(name).indexOf('classmove_') === 0) {
      var moveCount = 0;
      for (var mc = parts.length - 1; mc >= 0; mc--) {
        if (parts[mc]._classMove && ++moveCount > CLASS_MOVE_POOL_CAP) parts.splice(mc, 1);
      }
    }
    if (parts.length) start();
    return true;
  }

  window.hohFx = {
    available: !reduced,
    emit: emit,
    /* R523: the catalog contract, exported so the verb harness and the
       battle playback ask the same question the renderer answers. */
    catalogRenderable: catalogRenderable,
    /* R692: exported so a proof can assert what a glyph actually DRAWS. The
       gear drew a plain rectangle for a whole wave across 149 recipes and no
       test could see it, because every check measured the spawn and nothing
       measured the path. */
    glyphDraw: catalogGlyphDraw,
    catalogTemplates: function () { return Object.keys(CATALOG_TEMPLATES).sort(); },
    classCardPresentation: classCardPresentation,
    classCardPresentationIds: classCardPresentationIds,
    classCardOperationIds: classCardOperationIds,
    has: function (name) { return name === 'classcard.fx' || own(EFFECTS, name) || !!abilityIdFromPresentation(name); },
    presentationIds: function () { return PRESENTATION_IDS.slice(); },
    abilityPresentationIds: abilityPresentationIds,
    presentation: presentationDescriptor,
    limits: function () { return { pool: PART_CAP, semanticPerEmit: PRESENTATION_MOTE_CAP + 1,
      abilityPerEmit: ABILITY_MOTE_CAP + 1, classCardPerEmit: CLASS_CARD_MOTE_CAP,
      classMovePerEmit: CLASS_MOVE_PART_CAP, classMovePool: CLASS_MOVE_POOL_CAP }; },
    define: function (name, def) {
      /* Stable presentation ids are data contracts and cannot be replaced by
         late-loading scripts. New non-reserved effects remain extensible. */
      if (!name || own(PRESENTATION_RECIPES, name) || abilityIdFromPresentation(name) || !def ||
          typeof def.spawn !== 'function' || typeof def.step !== 'function' ||
          typeof def.draw !== 'function') return false;
      EFFECTS[name] = def;
      return true;
    },
    count: function () { return parts.length; },
    /* R638: the live particles, as flat copies, so a proof can measure the
       GEOMETRY a player sees and not only how many specks there are. Copies
       rather than the array itself, because handing out a live handle to
       internal state is exactly what R626 was paid for. Read-only by
       construction: mutating what comes back changes nothing here. */
    peek: function () {
      var out = [], i, p, k, c;
      for (i = 0; i < parts.length; i++) {
        p = parts[i]; c = {};
        for (k in p) if (Object.prototype.hasOwnProperty.call(p, k)) c[k] = p[k];
        out.push(c);
      }
      return out;
    },
    clear: function () { parts.length = 0; stop(); }
  };
  function motionApply() {
    reduced = osReduced || pageReduced();
    if (window.hohFx.available !== !reduced) window.hohFx.available = !reduced;
    if (reduced && (parts.length || raf)) { parts.length = 0; stop(); }
  }
  function motionChanged(e) {
    osReduced = !!(e && e.matches);
    motionApply();
  }
  try {
    if (motionQuery && motionQuery.addEventListener) motionQuery.addEventListener('change', motionChanged);
    else if (motionQuery && motionQuery.addListener) motionQuery.addListener(motionChanged);
  } catch (_) {}
  /* the page's Motion setting can flip mid-fight: its class is watched, and live particles clear the moment it turns on */
  try {
    if (typeof MutationObserver === 'function') new MutationObserver(motionApply).observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
  } catch (_) {}
})();
